import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  ArrowLeft, Store, MapPin, Calendar, Edit, Loader2, Plus, Trash2,
  CalendarClock, CheckCircle2, Circle, Clock, Sparkles,
  Paintbrush, Upload, Image as ImageIcon, Bookmark, BookmarkCheck, RefreshCw, Check, X,
  Wrench, DollarSign, ExternalLink, Globe, ArrowRight, Package, Users, BarChart3, FolderOpen,
  ZoomIn, Tag, ChevronLeft, ChevronRight, Pencil, Sofa, Filter, Search, Link, Share2, Copy, ToggleLeft, ToggleRight, CopyPlus
} from "lucide-react";
import StoreStaffTab from "@/components/store/StoreStaffTab";
import StoreFinancialsTab from "@/components/store/StoreFinancialsTab";
import StoreDocumentsTab from "@/components/store/StoreDocumentsTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: "planning", label: "Planning", color: "bg-amber-100 text-amber-700" },
  { value: "build_out", label: "Build Out", color: "bg-blue-100 text-blue-700" },
  { value: "open", label: "Open", color: "bg-emerald-100 text-emerald-700" },
  { value: "closed", label: "Closed", color: "bg-stone-200 text-stone-600" },
];

const TASK_CATEGORIES = [
  { value: "permits", label: "Permits & Licensing" },
  { value: "construction", label: "Construction & Build-out" },
  { value: "equipment", label: "Equipment" },
  { value: "hiring", label: "Hiring & Training" },
  { value: "training", label: "Training" },
  { value: "marketing", label: "Marketing" },
  { value: "operations", label: "Operations" },
  { value: "financial", label: "Financial" },
  { value: "design", label: "Design" },
  { value: "technology", label: "Technology" },
  { value: "general", label: "General" },
];

const CATEGORY_COLORS = {
  permits: "bg-red-100 text-red-700 border-red-200",
  construction: "bg-orange-100 text-orange-700 border-orange-200",
  equipment: "bg-blue-100 text-blue-700 border-blue-200",
  hiring: "bg-violet-100 text-violet-700 border-violet-200",
  training: "bg-indigo-100 text-indigo-700 border-indigo-200",
  marketing: "bg-pink-100 text-pink-700 border-pink-200",
  operations: "bg-stone-100 text-stone-700 border-stone-200",
  financial: "bg-emerald-100 text-emerald-700 border-emerald-200",
  design: "bg-amber-100 text-amber-700 border-amber-200",
  technology: "bg-cyan-100 text-cyan-700 border-cyan-200",
  general: "bg-stone-100 text-stone-600 border-stone-200",
};

const EQUIPMENT_CATEGORIES = [
  { value: "espresso", label: "Espresso Machines" },
  { value: "grinder", label: "Grinders" },
  { value: "brewing", label: "Brewing Equipment" },
  { value: "refrigeration", label: "Refrigeration" },
  { value: "furniture", label: "Furniture & Fixtures" },
  { value: "pos", label: "POS & Technology" },
  { value: "smallwares", label: "Smallwares" },
  { value: "signage", label: "Signage & Branding" },
  { value: "plumbing", label: "Plumbing & Electrical" },
  { value: "other", label: "Other" },
];

const EQUIPMENT_STATUS_OPTIONS = [
  { value: "needed", label: "Needed", color: "bg-amber-100 text-amber-700" },
  { value: "researching", label: "Researching", color: "bg-blue-100 text-blue-700" },
  { value: "ordered", label: "Ordered", color: "bg-violet-100 text-violet-700" },
  { value: "shipped", label: "Shipped", color: "bg-cyan-100 text-cyan-700" },
  { value: "received", label: "Received", color: "bg-emerald-100 text-emerald-700" },
  { value: "installed", label: "Installed", color: "bg-stone-200 text-stone-700" },
];

// Furniture constants
const FURNITURE_TAGS = [
  { value: "chairs", label: "Chairs" },
  { value: "tables", label: "Tables" },
  { value: "sofas", label: "Sofas" },
  { value: "mirrors", label: "Mirrors" },
  { value: "lights", label: "Lights" },
  { value: "shelving", label: "Shelving" },
  { value: "counters", label: "Counters" },
  { value: "stools", label: "Stools" },
  { value: "benches", label: "Benches" },
  { value: "plants", label: "Plants" },
  { value: "artwork", label: "Artwork" },
  { value: "rugs", label: "Rugs" },
  { value: "storage", label: "Storage" },
  { value: "other", label: "Other" },
];

const FURNITURE_STATUS_OPTIONS = [
  { value: "potential", label: "Potential", color: "bg-stone-100 text-stone-600" },
  { value: "selected", label: "Selected", color: "bg-blue-100 text-blue-700" },
  { value: "ordered", label: "Ordered", color: "bg-violet-100 text-violet-700" },
  { value: "delivered", label: "Delivered", color: "bg-emerald-100 text-emerald-700" },
  { value: "rejected", label: "Rejected", color: "bg-red-100 text-red-700" },
];

const emptyFurnitureForm = {
  name: "", description: "", price: "", lead_time_weeks: "", quantity: 1,
  vendor: "", dimensions: "", material: "", color: "", url: "", notes: "", tags: [], status: "potential",
};

const emptyEquipmentForm = {
  name: "", category: "other", url: "", brand: "", model_name: "",
  lead_time_days: "", cost: "", quantity: 1,
  status: "needed", vendor: "", notes: "",
  spec_dimensions: "", spec_weight: "", spec_power: "", spec_capacity: "",
};

export default function StoreDetail() {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Edit store dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({});

  // Timeline state
  const [tasks, setTasks] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskForm, setTaskForm] = useState({ title: "", description: "", category: "general", due_date: "", status: "pending" });
  const [filterCategory, setFilterCategory] = useState("all");

  // Equipment state
  const [storeEquipment, setStoreEquipment] = useState([]);
  const [equipmentDialogOpen, setEquipmentDialogOpen] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [equipmentForm, setEquipmentForm] = useState(emptyEquipmentForm);
  const [equipmentStep, setEquipmentStep] = useState("url");
  const [scanUrl, setScanUrl] = useState("");
  const [scanning, setScanning] = useState(false);
  const [filterEquipmentStatus, setFilterEquipmentStatus] = useState("all");
  const [scrapedEquipmentImages, setScrapedEquipmentImages] = useState([]);
  const [selectedEquipmentImages, setSelectedEquipmentImages] = useState([]);
  const [pendingEquipmentImages, setPendingEquipmentImages] = useState([]); // Files to upload
  const [existingEquipmentImages, setExistingEquipmentImages] = useState([]); // Already uploaded (for editing)
  const pendingEquipmentImageRef = useRef(null);
  
  // Assign equipment from catalog state
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [availableEquipment, setAvailableEquipment] = useState([]);
  const [selectedMasterIds, setSelectedMasterIds] = useState([]);
  const [assigningEquipment, setAssigningEquipment] = useState(false);

  // Interiors state
  const [storeImages, setStoreImages] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [savedDesigns, setSavedDesigns] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedStoreImgs, setSelectedStoreImgs] = useState([]);
  const [selectedEquipIds, setSelectedEquipIds] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [generatingDesign, setGeneratingDesign] = useState(false);
  const [currentDesign, setCurrentDesign] = useState(null);
  const [refinementPrompt, setRefinementPrompt] = useState("");
  const [interiorTab, setInteriorTab] = useState("gallery");
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [editingImage, setEditingImage] = useState(null);
  const [editImageLabel, setEditImageLabel] = useState("");
  const [editImageTags, setEditImageTags] = useState([]);
  const [editTagInput, setEditTagInput] = useState("");
  const [savingImage, setSavingImage] = useState(false);
  
  // Reference files for design generation
  const [referenceFiles, setReferenceFiles] = useState([]);
  const referenceFileRef = useRef(null);
  
  // Furniture state
  const [furniture, setFurniture] = useState([]);
  const [furnitureDialogOpen, setFurnitureDialogOpen] = useState(false);
  const [editingFurniture, setEditingFurniture] = useState(null);
  const [furnitureForm, setFurnitureForm] = useState(emptyFurnitureForm);
  const [furnitureStep, setFurnitureStep] = useState("url"); // url or manual
  const [furnitureScanUrl, setFurnitureScanUrl] = useState("");
  const [scanningFurniture, setScanningFurniture] = useState(false);
  const [filterFurnitureStatus, setFilterFurnitureStatus] = useState("all");
  const [filterFurnitureTag, setFilterFurnitureTag] = useState("all");
  const [furnitureSearchQuery, setFurnitureSearchQuery] = useState("");
  const furnitureImageRef = useRef(null);
  const [scrapedFurnitureImages, setScrapedFurnitureImages] = useState([]);
  const [selectedScrapedImages, setSelectedScrapedImages] = useState([]);
  const [importingImages, setImportingImages] = useState(false);
  const [pendingFurnitureImages, setPendingFurnitureImages] = useState([]); // Files to upload
  const [existingFurnitureImages, setExistingFurnitureImages] = useState([]); // Already uploaded (for editing)
  const pendingFurnitureImageRef = useRef(null);
  const [furnitureQtyModalItem, setFurnitureQtyModalItem] = useState(null);
  const [furnitureSelectedQty, setFurnitureSelectedQty] = useState(1);
  
  // Furniture sharing state
  const [furnitureShare, setFurnitureShare] = useState(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [creatingShare, setCreatingShare] = useState(false);
  
  const fileRef = useRef(null);
  const multiFileRef = useRef(null);

  useEffect(() => {
    if (storeId) {
      fetchStore();
      fetchTasks();
      fetchEquipmentData();
      fetchInteriorData();
      fetchFurniture();
    }
  }, [storeId]);

  const fetchStore = async () => {
    try {
      const res = await axios.get(`${API}/stores/${storeId}`);
      setStore(res.data);
      setEditForm(res.data);
    } catch (e) {
      toast.error("Store not found");
      navigate("/stores");
    } finally {
      setLoading(false);
    }
  };

  const fetchTasks = async () => {
    try {
      const res = await axios.get(`${API}/tasks`, { params: { store_id: storeId } });
      setTasks(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchEquipmentData = async () => {
    try {
      const params = { store_id: storeId };
      if (filterEquipmentStatus !== "all") params.status = filterEquipmentStatus;
      const res = await axios.get(`${API}/equipment`, { params });
      setStoreEquipment(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (storeId) fetchEquipmentData();
  }, [filterEquipmentStatus]);

  // Fetch furniture with filters
  const fetchFurniture = async () => {
    try {
      const params = {};
      if (filterFurnitureStatus !== "all") params.status = filterFurnitureStatus;
      if (filterFurnitureTag !== "all") params.tag = filterFurnitureTag;
      const res = await axios.get(`${API}/stores/${storeId}/furniture`, { params });
      setFurniture(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (storeId) fetchFurniture();
  }, [filterFurnitureStatus, filterFurnitureTag]);

  const fetchInteriorData = async () => {
    try {
      const [imgRes, eqRes, dRes, sRes] = await Promise.all([
        axios.get(`${API}/stores/${storeId}/images`),
        axios.get(`${API}/equipment?store_id=${storeId}`),
        axios.get(`${API}/interiors/designs/${storeId}`),
        axios.get(`${API}/interiors/designs/${storeId}?saved_only=true`),
      ]);
      setStoreImages(imgRes.data);
      setEquipment(eqRes.data);
      setDesigns(dRes.data);
      setSavedDesigns(sRes.data);
    } catch (e) { console.error(e); }
  };

  // Equipment handlers
  const scanProductUrl = async () => {
    if (!scanUrl.trim()) { toast.error("Enter a URL first"); return; }
    setScanning(true);
    setScrapedEquipmentImages([]);
    setSelectedEquipmentImages([]);
    try {
      const res = await axios.post(`${API}/equipment/scan-url`, { url: scanUrl.trim() });
      const r = res.data;
      setEquipmentForm({
        name: r.name || "",
        category: r.category || "other",
        url: r.url || scanUrl.trim(),
        brand: r.brand || "",
        model_name: r.model_name || "",
        spec_dimensions: r.specs?.dimensions || "",
        spec_weight: r.specs?.weight || "",
        spec_power: r.specs?.power || "",
        spec_capacity: r.specs?.capacity || "",
        lead_time_days: r.lead_time_days?.toString() || "",
        cost: r.cost?.toString() || "",
        quantity: 1,
        status: "needed",
        vendor: r.vendor || "",
        notes: r.description || "",
      });
      // Store scraped images for selection
      if (r.scraped_images?.length > 0) {
        setScrapedEquipmentImages(r.scraped_images);
        setSelectedEquipmentImages(r.scraped_images.slice(0, 3));
      }
      setEquipmentStep("form");
      toast.success(`Product details extracted! ${r.scraped_images?.length || 0} images found.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to scan URL");
    } finally {
      setScanning(false);
    }
  };

  const handleEquipmentSubmit = async (e) => {
    e.preventDefault();
    if (!equipmentForm.name.trim()) { toast.error("Equipment name is required"); return; }

    const specs = {};
    if (equipmentForm.spec_dimensions) specs.dimensions = equipmentForm.spec_dimensions;
    if (equipmentForm.spec_weight) specs.weight = equipmentForm.spec_weight;
    if (equipmentForm.spec_power) specs.power = equipmentForm.spec_power;
    if (equipmentForm.spec_capacity) specs.capacity = equipmentForm.spec_capacity;

    const payload = {
      name: equipmentForm.name, category: equipmentForm.category, url: equipmentForm.url,
      brand: equipmentForm.brand, model_name: equipmentForm.model_name, specs,
      lead_time_days: equipmentForm.lead_time_days ? parseInt(equipmentForm.lead_time_days) : null,
      cost: equipmentForm.cost ? parseFloat(equipmentForm.cost) : null,
      quantity: parseInt(equipmentForm.quantity) || 1,
      status: equipmentForm.status, vendor: equipmentForm.vendor, notes: equipmentForm.notes,
      store_id: storeId,
    };

    try {
      let equipId;
      if (editingEquipment) {
        await axios.put(`${API}/equipment/${editingEquipment.id}`, payload);
        equipId = editingEquipment.id;
        toast.success("Equipment updated");
      } else {
        const res = await axios.post(`${API}/equipment`, payload);
        equipId = res.data.id;
        toast.success("Equipment added");
      }
      
      // Upload pending local images
      if (pendingEquipmentImages.length > 0) {
        toast.info(`Uploading ${pendingEquipmentImages.length} images...`);
        for (const file of pendingEquipmentImages) {
          try {
            const fd = new FormData();
            fd.append("file", file);
            await axios.post(`${API}/equipment/${equipId}/image`, fd);
          } catch (e) {
            console.error(`Failed to upload image: ${file.name}`, e);
          }
        }
        toast.success("Images uploaded!");
      }
      
      // Import selected scraped images (only for new items)
      if (selectedEquipmentImages.length > 0 && !editingEquipment) {
        toast.info(`Importing ${selectedEquipmentImages.length} web images...`);
        let successCount = 0;
        for (const imgUrl of selectedEquipmentImages) {
          try {
            await axios.post(`${API}/equipment/${equipId}/image-url`, { url: imgUrl });
            successCount++;
          } catch (err) {
            console.error(`Failed to import image: ${imgUrl}`, err);
          }
        }
        if (successCount > 0) {
          toast.success(`${successCount} web image${successCount > 1 ? 's' : ''} imported!`);
        } else if (selectedEquipmentImages.length > 0) {
          toast.error("Could not import web images - some sites block downloads.");
        }
      }
      
      closeEquipmentDialog();
      fetchEquipmentData();
      fetchInteriorData();
    } catch (e) { toast.error("Failed to save equipment"); }
  };

  const deleteEquipment = async (id) => {
    try {
      await axios.delete(`${API}/equipment/${id}`);
      toast.success("Equipment deleted");
      fetchEquipmentData();
      fetchInteriorData();
    } catch (e) { toast.error("Failed to delete"); }
  };

  const uploadEquipmentImage = async (equipId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/equipment/${equipId}/image`, fd);
      toast.success("Image uploaded");
      fetchEquipmentData();
    } catch (e) { toast.error("Upload failed"); }
  };

  const deleteEquipmentImage = async (equipId, imageId) => {
    try {
      await axios.delete(`${API}/equipment/${equipId}/image/${imageId}`);
      toast.success("Image deleted");
      fetchEquipmentData();
    } catch (e) { toast.error("Failed"); }
  };

  // Assign equipment from master catalog
  const openAssignDialog = async () => {
    try {
      const res = await axios.get(`${API}/stores/${storeId}/available-equipment`);
      setAvailableEquipment(res.data);
      setSelectedMasterIds([]);
      setAssignDialogOpen(true);
    } catch (e) {
      toast.error("Failed to load equipment catalog");
    }
  };

  const toggleMasterSelection = (masterId) => {
    setSelectedMasterIds(prev => 
      prev.includes(masterId) 
        ? prev.filter(id => id !== masterId)
        : [...prev, masterId]
    );
  };

  const assignSelectedEquipment = async () => {
    if (selectedMasterIds.length === 0) {
      toast.error("Select at least one item");
      return;
    }
    setAssigningEquipment(true);
    try {
      let success = 0;
      for (const masterId of selectedMasterIds) {
        try {
          await axios.post(`${API}/equipment/assign-to-store`, {
            master_id: masterId,
            store_id: storeId,
            quantity: 1,
            status: "needed"
          });
          success++;
        } catch (e) {
          console.error(`Failed to assign ${masterId}:`, e);
        }
      }
      toast.success(`${success} item${success > 1 ? 's' : ''} assigned to store`);
      setAssignDialogOpen(false);
      fetchEquipmentData();
    } catch (e) {
      toast.error("Failed to assign equipment");
    } finally {
      setAssigningEquipment(false);
    }
  };

  const unassignEquipment = async (equipId) => {
    try {
      await axios.delete(`${API}/equipment/unassign/${equipId}`);
      toast.success("Equipment removed from store");
      fetchEquipmentData();
    } catch (e) {
      toast.error("Failed to remove equipment");
    }
  };

  const openEditEquipment = (eq) => {
    setEditingEquipment(eq);
    setEquipmentForm({
      name: eq.name, category: eq.category, url: eq.url || "",
      brand: eq.brand || "", model_name: eq.model_name || "",
      spec_dimensions: eq.specs?.dimensions || "", spec_weight: eq.specs?.weight || "",
      spec_power: eq.specs?.power || "", spec_capacity: eq.specs?.capacity || "",
      lead_time_days: eq.lead_time_days?.toString() || "", cost: eq.cost?.toString() || "",
      quantity: eq.quantity || 1, status: eq.status || "needed",
      vendor: eq.vendor || "", notes: eq.notes || "",
    });
    setExistingEquipmentImages(eq.images || []);
    setPendingEquipmentImages([]);
    setEquipmentStep("form");
    setScanUrl(eq.url || "");
    setEquipmentDialogOpen(true);
  };

  const openNewEquipment = () => {
    setEditingEquipment(null);
    setEquipmentForm(emptyEquipmentForm);
    setEquipmentStep("url");
    setScanUrl("");
    setPendingEquipmentImages([]);
    setExistingEquipmentImages([]);
    setEquipmentDialogOpen(true);
  };

  const duplicateEquipment = (eq) => {
    // Copy equipment data but as a new item (not editing)
    setEditingEquipment(null);
    setEquipmentForm({
      name: `${eq.name} (Copy)`,
      category: eq.category || "other",
      url: eq.url || "",
      brand: eq.brand || "",
      model_name: eq.model_name || "",
      spec_dimensions: eq.specs?.dimensions || "",
      spec_weight: eq.specs?.weight || "",
      spec_power: eq.specs?.power || "",
      spec_capacity: eq.specs?.capacity || "",
      lead_time_days: eq.lead_time_days?.toString() || "",
      cost: eq.cost?.toString() || "",
      quantity: eq.quantity || 1,
      status: "needed", // Reset status to needed for new item
      vendor: eq.vendor || "",
      notes: eq.notes || "",
    });
    setPendingEquipmentImages([]);
    setExistingEquipmentImages([]); // Don't copy images, user can add new ones
    setEquipmentStep("form");
    setScanUrl(eq.url || "");
    setEquipmentDialogOpen(true);
    toast.info("Equipment duplicated - modify and save as new");
  };

  const closeEquipmentDialog = () => {
    setEquipmentDialogOpen(false);
    setEquipmentForm(emptyEquipmentForm);
    setEditingEquipment(null);
    setEquipmentStep("url");
    setScanUrl("");
    setScrapedEquipmentImages([]);
    setSelectedEquipmentImages([]);
    setPendingEquipmentImages([]);
    setExistingEquipmentImages([]);
  };

  const skipToManualEquipment = () => {
    setEquipmentForm(emptyEquipmentForm);
    setPendingEquipmentImages([]);
    setEquipmentStep("form");
  };

  const addPendingEquipmentImage = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setPendingEquipmentImages(prev => [...prev, ...files]);
    }
    e.target.value = '';
  };

  const removePendingEquipmentImage = (index) => {
    setPendingEquipmentImages(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingEquipmentImage = async (imageId) => {
    if (!editingEquipment) return;
    try {
      await axios.delete(`${API}/equipment/${editingEquipment.id}/image/${imageId}`);
      setExistingEquipmentImages(prev => prev.filter(img => img.id !== imageId));
      toast.success("Image removed");
    } catch (e) {
      toast.error("Failed to remove image");
    }
  };

  const getEquipmentStatusBadge = (status) => {
    const opt = EQUIPMENT_STATUS_OPTIONS.find(s => s.value === status);
    return opt ? (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${opt.color}`}>
        {opt.label}
      </span>
    ) : null;
  };

  const totalEquipmentCost = storeEquipment.reduce((sum, eq) => sum + (eq.cost || 0) * (eq.quantity || 1), 0);

  // ========== Furniture Handlers ==========
  
  const scanFurnitureUrl = async () => {
    if (!furnitureScanUrl.trim()) {
      toast.error("Please enter a URL");
      return;
    }
    setScanningFurniture(true);
    setScrapedFurnitureImages([]);
    setSelectedScrapedImages([]);
    try {
      const res = await axios.post(`${API}/furniture/scan-url`, { url: furnitureScanUrl });
      setFurnitureForm({
        ...emptyFurnitureForm,
        name: res.data.name || "",
        description: res.data.description || "",
        price: res.data.price?.toString() || "",
        lead_time_weeks: res.data.lead_time_weeks?.toString() || "",
        vendor: res.data.vendor || "",
        dimensions: res.data.dimensions || "",
        material: res.data.material || "",
        color: res.data.color || "",
        url: res.data.url || furnitureScanUrl,
        tags: res.data.tags || [],
        status: "potential",
      });
      // Store scraped images for selection
      if (res.data.scraped_images?.length > 0) {
        setScrapedFurnitureImages(res.data.scraped_images);
        setSelectedScrapedImages(res.data.scraped_images.slice(0, 3)); // Select first 3 by default
      }
      setFurnitureStep("form");
      toast.success(`Product info extracted! ${res.data.scraped_images?.length || 0} images found.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to scan URL");
    } finally {
      setScanningFurniture(false);
    }
  };

  const saveFurniture = async () => {
    if (!furnitureForm.name.trim()) {
      toast.error("Name is required");
      return;
    }
    try {
      const payload = {
        ...furnitureForm,
        store_id: storeId,
        price: furnitureForm.price ? parseFloat(furnitureForm.price) : null,
        lead_time_weeks: furnitureForm.lead_time_weeks ? parseInt(furnitureForm.lead_time_weeks) : null,
        quantity: parseInt(furnitureForm.quantity) || 1,
      };
      
      let furnitureId;
      if (editingFurniture) {
        await axios.put(`${API}/furniture/${editingFurniture.id}`, payload);
        furnitureId = editingFurniture.id;
        toast.success("Furniture updated");
      } else {
        const res = await axios.post(`${API}/stores/${storeId}/furniture`, payload);
        furnitureId = res.data.id;
        toast.success("Furniture added");
      }
      
      // Upload pending local images
      if (pendingFurnitureImages.length > 0) {
        toast.info(`Uploading ${pendingFurnitureImages.length} images...`);
        for (const file of pendingFurnitureImages) {
          try {
            const fd = new FormData();
            fd.append("file", file);
            await axios.post(`${API}/furniture/${furnitureId}/image`, fd);
          } catch (e) {
            console.error(`Failed to upload image: ${file.name}`, e);
          }
        }
        toast.success("Images uploaded!");
      }
      
      // Import selected scraped images (only for new items)
      if (selectedScrapedImages.length > 0 && !editingFurniture) {
        setImportingImages(true);
        toast.info(`Importing ${selectedScrapedImages.length} web images...`);
        let successCount = 0;
        for (const imgUrl of selectedScrapedImages) {
          try {
            await axios.post(`${API}/furniture/${furnitureId}/image-url`, { url: imgUrl });
            successCount++;
          } catch (e) {
            console.error(`Failed to import image: ${imgUrl}`, e);
          }
        }
        setImportingImages(false);
        if (successCount > 0) {
          toast.success(`${successCount} web image${successCount > 1 ? 's' : ''} imported!`);
        } else if (selectedScrapedImages.length > 0) {
          toast.error("Could not import web images - some sites block downloads.");
        }
      }
      
      closeFurnitureDialog();
      fetchFurniture();
    } catch (e) {
      toast.error("Failed to save furniture");
      setImportingImages(false);
    }
  };

  const deleteFurniture = async (id) => {
    if (!window.confirm("Delete this furniture item?")) return;
    try {
      await axios.delete(`${API}/furniture/${id}`);
      toast.success("Deleted");
      fetchFurniture();
    } catch (e) { toast.error("Failed"); }
  };

  const updateFurnitureStatus = async (id, newStatus, selectedQty = null) => {
    try {
      const payload = { status: newStatus };
      if (selectedQty !== null) {
        payload.selected_quantity = selectedQty;
      }
      await axios.patch(`${API}/furniture/${id}/status`, payload);
      toast.success(`Status updated to ${newStatus}`);
      fetchFurniture();
      setFurnitureQtyModalItem(null);
    } catch (e) { toast.error("Failed to update status"); }
  };

  const openFurnitureQtyModal = (item) => {
    setFurnitureQtyModalItem(item);
    setFurnitureSelectedQty(item.selected_quantity || 1);
  };

  const confirmFurnitureSelection = () => {
    if (furnitureQtyModalItem) {
      updateFurnitureStatus(furnitureQtyModalItem.id, "selected", furnitureSelectedQty);
    }
  };

  const uploadFurnitureImage = async (furnitureId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/furniture/${furnitureId}/image`, fd);
      toast.success("Image uploaded");
      fetchFurniture();
    } catch (e) { toast.error("Upload failed"); }
  };

  const deleteFurnitureImage = async (furnitureId, imageId) => {
    try {
      await axios.delete(`${API}/furniture/${furnitureId}/image/${imageId}`);
      toast.success("Image deleted");
      fetchFurniture();
    } catch (e) { toast.error("Failed"); }
  };

  const openEditFurniture = (item) => {
    setEditingFurniture(item);
    setFurnitureForm({
      name: item.name || "",
      description: item.description || "",
      price: item.price?.toString() || "",
      lead_time_weeks: item.lead_time_weeks?.toString() || "",
      quantity: item.quantity || 1,
      vendor: item.vendor || "",
      dimensions: item.dimensions || "",
      material: item.material || "",
      color: item.color || "",
      url: item.url || "",
      notes: item.notes || "",
      tags: item.tags || [],
      status: item.status || "potential",
    });
    setExistingFurnitureImages(item.images || []);
    setPendingFurnitureImages([]);
    setFurnitureStep("form");
    setFurnitureScanUrl(item.url || "");
    setFurnitureDialogOpen(true);
  };

  const openNewFurniture = () => {
    setEditingFurniture(null);
    setFurnitureForm(emptyFurnitureForm);
    setFurnitureStep("url");
    setFurnitureScanUrl("");
    setPendingFurnitureImages([]);
    setExistingFurnitureImages([]);
    setFurnitureDialogOpen(true);
  };

  const duplicateFurniture = (item) => {
    // Copy item data but as a new item (not editing)
    setEditingFurniture(null);
    setFurnitureForm({
      name: `${item.name} (Copy)`,
      description: item.description || "",
      price: item.price?.toString() || "",
      lead_time_weeks: item.lead_time_weeks?.toString() || "",
      quantity: item.quantity || 1,
      vendor: item.vendor || "",
      dimensions: item.dimensions || "",
      material: item.material || "",
      color: item.color || "",
      url: item.url || "",
      notes: item.notes || "",
      tags: item.tags || [],
      status: "potential", // Reset status to potential for new item
    });
    setPendingFurnitureImages([]);
    setExistingFurnitureImages([]); // Don't copy images, user can add new ones
    setFurnitureStep("form");
    setFurnitureScanUrl(item.url || "");
    setFurnitureDialogOpen(true);
    toast.info("Item duplicated - modify and save as new");
  };

  const closeFurnitureDialog = () => {
    setFurnitureDialogOpen(false);
    setFurnitureForm(emptyFurnitureForm);
    setEditingFurniture(null);
    setFurnitureStep("url");
    setFurnitureScanUrl("");
    setScrapedFurnitureImages([]);
    setSelectedScrapedImages([]);
    setPendingFurnitureImages([]);
    setExistingFurnitureImages([]);
  };

  const skipToManualFurniture = () => {
    setFurnitureForm(emptyFurnitureForm);
    setPendingFurnitureImages([]);
    setFurnitureStep("form");
  };

  const addPendingFurnitureImage = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setPendingFurnitureImages(prev => [...prev, ...files]);
    }
    e.target.value = '';
  };

  const removePendingFurnitureImage = (index) => {
    setPendingFurnitureImages(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingFurnitureImage = async (imageId) => {
    if (!editingFurniture) return;
    try {
      await axios.delete(`${API}/furniture/${editingFurniture.id}/image/${imageId}`);
      setExistingFurnitureImages(prev => prev.filter(img => img.id !== imageId));
      toast.success("Image removed");
    } catch (e) {
      toast.error("Failed to remove image");
    }
  };

  const getFurnitureStatusBadge = (status) => {
    const opt = FURNITURE_STATUS_OPTIONS.find(s => s.value === status);
    return opt ? (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${opt.color}`}>
        {opt.label}
      </span>
    ) : null;
  };

  const toggleFurnitureTag = (tagValue) => {
    setFurnitureForm(prev => ({
      ...prev,
      tags: prev.tags.includes(tagValue) 
        ? prev.tags.filter(t => t !== tagValue)
        : [...prev.tags, tagValue]
    }));
  };

  // Filter furniture by search query
  const filteredFurniture = furniture.filter(item => {
    if (!furnitureSearchQuery.trim()) return true;
    const query = furnitureSearchQuery.toLowerCase();
    return item.name?.toLowerCase().includes(query) ||
           item.vendor?.toLowerCase().includes(query) ||
           item.description?.toLowerCase().includes(query) ||
           item.tags?.some(t => t.toLowerCase().includes(query));
  });

  const totalFurnitureCost = furniture.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0);

  // Furniture share functions
  const fetchFurnitureShare = async () => {
    try {
      const res = await axios.get(`${API}/stores/${storeId}/furniture/share`);
      setFurnitureShare(res.data.share_token ? res.data : null);
    } catch (e) { console.error(e); }
  };

  const createFurnitureShare = async () => {
    setCreatingShare(true);
    try {
      const res = await axios.post(`${API}/stores/${storeId}/furniture/share`);
      setFurnitureShare(res.data);
      toast.success("Share link created!");
    } catch (e) { toast.error("Failed to create share link"); }
    finally { setCreatingShare(false); }
  };

  const toggleShareActive = async () => {
    try {
      const res = await axios.patch(`${API}/stores/${storeId}/furniture/share`, { 
        is_active: !furnitureShare.is_active,
        allow_voting: furnitureShare.allow_voting 
      });
      setFurnitureShare(res.data);
      toast.success(res.data.is_active ? "Share link enabled" : "Share link disabled");
    } catch (e) { toast.error("Failed to update"); }
  };

  const toggleVoting = async () => {
    try {
      const res = await axios.patch(`${API}/stores/${storeId}/furniture/share`, { 
        is_active: furnitureShare.is_active,
        allow_voting: !furnitureShare.allow_voting 
      });
      setFurnitureShare(res.data);
      toast.success(res.data.allow_voting ? "Voting enabled" : "Voting disabled");
    } catch (e) { toast.error("Failed to update"); }
  };

  const copyShareLink = () => {
    const link = `${window.location.origin}/furniture/${furnitureShare.share_token}`;
    navigator.clipboard.writeText(link);
    toast.success("Link copied to clipboard!");
  };

  useEffect(() => {
    if (storeId && interiorTab === "furniture") fetchFurnitureShare();
  }, [storeId, interiorTab]);

  // ========== End Furniture Handlers ==========

  // Store edit handlers
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/stores/${storeId}`, editForm);
      toast.success("Store updated");
      setEditDialogOpen(false);
      fetchStore();
    } catch (e) { toast.error("Failed to update store"); }
  };

  const getStatusBadge = (status) => {
    const opt = STATUS_OPTIONS.find((s) => s.value === status);
    return opt ? (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${opt.color}`}>
        {opt.label}
      </span>
    ) : null;
  };

  // Timeline handlers
  const generateTimeline = async () => {
    if (!store?.target_open_date) {
      toast.error("Set a target opening date first");
      return;
    }
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/stores/${storeId}/generate-timeline`);
      toast.success(`${res.data.tasks_created} tasks generated!`);
      fetchTasks();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate timeline");
    } finally { setGenerating(false); }
  };

  const handleTaskSubmit = async (e) => {
    e.preventDefault();
    if (!taskForm.title.trim()) { toast.error("Task title is required"); return; }
    try {
      const payload = { ...taskForm, store_id: storeId, source: "manual" };
      if (editingTask) {
        await axios.put(`${API}/tasks/${editingTask.id}`, payload);
        toast.success("Task updated");
      } else {
        await axios.post(`${API}/tasks`, payload);
        toast.success("Task added");
      }
      setTaskDialogOpen(false);
      setTaskForm({ title: "", description: "", category: "general", due_date: "", status: "pending" });
      setEditingTask(null);
      fetchTasks();
    } catch (e) { toast.error("Failed to save task"); }
  };

  const toggleTaskStatus = async (task) => {
    const next = task.status === "completed" ? "pending" : task.status === "pending" ? "in_progress" : "completed";
    try {
      await axios.put(`${API}/tasks/${task.id}`, { status: next });
      fetchTasks();
    } catch (e) { toast.error("Failed to update status"); }
  };

  const deleteTask = async (id) => {
    try {
      await axios.delete(`${API}/tasks/${id}`);
      toast.success("Task deleted");
      fetchTasks();
    } catch (e) { toast.error("Failed to delete"); }
  };

  const openEditTask = (task) => {
    setEditingTask(task);
    setTaskForm({
      title: task.title, description: task.description || "",
      category: task.category || "general", due_date: task.due_date || "",
      status: task.status || "pending",
    });
    setTaskDialogOpen(true);
  };

  // Interior handlers
  const uploadStoreImages = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    
    let successCount = 0;
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("label", file.name);
      try {
        await axios.post(`${API}/stores/${storeId}/images`, fd);
        successCount++;
      } catch (e) { console.error(e); }
    }
    
    if (successCount > 0) {
      toast.success(`${successCount} image${successCount > 1 ? 's' : ''} uploaded`);
      fetchInteriorData();
    } else {
      toast.error("Upload failed");
    }
    setUploading(false);
    if (multiFileRef.current) multiFileRef.current.value = "";
  };

  const deleteStoreImage = async (imageId) => {
    try {
      await axios.delete(`${API}/stores/${storeId}/images/${imageId}`);
      toast.success("Deleted");
      fetchInteriorData();
      // Close lightbox if the deleted image was being viewed
      if (lightboxOpen && storeImages[lightboxIndex]?.id === imageId) {
        setLightboxOpen(false);
      }
    } catch (e) { toast.error("Failed"); }
  };

  // Lightbox functions
  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const prevImage = () => {
    setLightboxIndex(prev => (prev > 0 ? prev - 1 : storeImages.length - 1));
  };

  const nextImage = () => {
    setLightboxIndex(prev => (prev < storeImages.length - 1 ? prev + 1 : 0));
  };

  const openEditImage = (img) => {
    setEditingImage(img);
    setEditImageLabel(img.label || "");
    setEditImageTags(img.tags || []);
    setEditTagInput("");
  };

  const closeEditImage = () => {
    setEditingImage(null);
    setEditImageLabel("");
    setEditImageTags([]);
    setEditTagInput("");
  };

  const addImageTag = (tag) => {
    const t = tag.trim().toLowerCase();
    if (t && !editImageTags.includes(t)) {
      setEditImageTags(prev => [...prev, t]);
    }
    setEditTagInput("");
  };

  const removeImageTag = (tag) => {
    setEditImageTags(prev => prev.filter(t => t !== tag));
  };

  const saveImageChanges = async () => {
    if (!editingImage) return;
    setSavingImage(true);
    try {
      await axios.patch(`${API}/stores/${storeId}/images/${editingImage.id}`, {
        label: editImageLabel,
        tags: editImageTags
      });
      toast.success("Image updated");
      fetchInteriorData();
      closeEditImage();
    } catch (e) { toast.error("Failed to update"); }
    finally { setSavingImage(false); }
  };

  const toggleStoreImg = (id) => {
    setSelectedStoreImgs(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const toggleEquip = (id) => {
    setSelectedEquipIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const generateDesign = async (isRefinement = false) => {
    const p = isRefinement ? refinementPrompt : prompt;
    if (!p.trim()) { toast.error("Enter a design prompt"); return; }
    setGeneratingDesign(true);
    try {
      const fd = new FormData();
      fd.append("store_id", storeId);
      fd.append("prompt", p);
      fd.append("store_image_ids", selectedStoreImgs.join(","));
      fd.append("equipment_ids", selectedEquipIds.join(","));
      if (isRefinement && currentDesign) fd.append("previous_design_id", currentDesign.id);
      
      // Append reference files
      for (const file of referenceFiles) {
        fd.append("reference_files", file);
      }
      
      const res = await axios.post(`${API}/interiors/generate`, fd, { timeout: 120000 });
      setCurrentDesign(res.data);
      setRefinementPrompt("");
      setReferenceFiles([]); // Clear after successful generation
      toast.success("Design generated!");
      fetchInteriorData();
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
    finally { setGeneratingDesign(false); }
  };

  const addReferenceFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setReferenceFiles(prev => [...prev, ...files].slice(0, 5)); // Max 5 reference files
    }
    e.target.value = "";
  };

  const removeReferenceFile = (index) => {
    setReferenceFiles(prev => prev.filter((_, i) => i !== index));
  };

  const saveDesign = async (designId) => {
    try {
      await axios.patch(`${API}/interiors/designs/${designId}/save`);
      toast.success("Design saved!");
      fetchInteriorData();
      if (currentDesign?.id === designId) setCurrentDesign({ ...currentDesign, saved: true });
    } catch (e) { toast.error("Failed"); }
  };

  const unsaveDesign = async (designId) => {
    try {
      await axios.patch(`${API}/interiors/designs/${designId}/unsave`);
      toast.success("Design unsaved");
      fetchInteriorData();
      if (currentDesign?.id === designId) setCurrentDesign({ ...currentDesign, saved: false });
    } catch (e) { toast.error("Failed"); }
  };

  const deleteDesign = async (designId) => {
    try {
      await axios.delete(`${API}/interiors/designs/${designId}`);
      toast.success("Design deleted");
      fetchInteriorData();
      if (currentDesign?.id === designId) setCurrentDesign(null);
    } catch (e) { toast.error("Failed"); }
  };

  // Computed values
  const completedTasks = tasks.filter(t => t.status === "completed").length;
  const totalTasks = tasks.length;
  const progressPct = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  const daysUntil = store?.target_open_date
    ? Math.ceil((new Date(store.target_open_date) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  const filteredTasks = filterCategory === "all" ? tasks : tasks.filter(t => t.category === filterCategory);
  const sortedTasks = [...filteredTasks].sort((a, b) => {
    if (!a.due_date) return 1;
    if (!b.due_date) return -1;
    return new Date(a.due_date) - new Date(b.due_date);
  });

  const grouped = {};
  sortedTasks.forEach(task => {
    const key = task.due_date ? task.due_date.slice(0, 7) : "no-date";
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(task);
  });

  const getStatusIcon = (status) => {
    if (status === "completed") return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
    if (status === "in_progress") return <Clock className="w-5 h-5 text-amber-500" />;
    return <Circle className="w-5 h-5 text-stone-300" />;
  };

  const formatMonth = (key) => {
    if (key === "no-date") return "No Due Date";
    const [y, m] = key.split("-");
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return `${months[parseInt(m) - 1]} ${y}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
      </div>
    );
  }

  if (!store) return null;

  return (
    <div className="min-h-screen px-6 md:px-12 lg:px-16 py-10" data-testid="store-detail">
      {/* Back Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate("/stores")}
        className="text-stone-500 hover:text-stone-900 mb-4 -ml-2"
        data-testid="back-to-stores"
      >
        <ArrowLeft className="w-4 h-4 mr-1" /> All Stores
      </Button>

      {/* Header */}
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-stone-900 rounded-xl flex items-center justify-center">
              <Store className="w-5 h-5 text-stone-50" />
            </div>
            {getStatusBadge(store.status)}
          </div>
          <h1
            data-testid="store-name"
            className="text-4xl md:text-5xl tracking-tight font-semibold text-stone-900"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {store.name}
          </h1>
          {(store.city || store.address) && (
            <p className="text-base text-stone-500 mt-1 flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              {store.address && `${store.address}, `}
              {[store.city, store.state].filter(Boolean).join(", ")}
            </p>
          )}
        </div>
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" data-testid="edit-store-btn">
              <Edit className="w-4 h-4 mr-2" /> Edit Store
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>Edit Store</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditSubmit} className="space-y-4 mt-4">
              <div>
                <Label className="text-sm font-medium text-stone-700">Store Name *</Label>
                <Input
                  value={editForm.name || ""}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-medium text-stone-700">City</Label>
                  <Input
                    value={editForm.city || ""}
                    onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-stone-700">State</Label>
                  <Input
                    value={editForm.state || ""}
                    onChange={(e) => setEditForm({ ...editForm, state: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium text-stone-700">Address</Label>
                <Input
                  value={editForm.address || ""}
                  onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-stone-700">Status</Label>
                <Select value={editForm.status || "planning"} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium text-stone-700">Target Opening Date</Label>
                <Input
                  type="date"
                  value={editForm.target_open_date || ""}
                  onChange={(e) => setEditForm({ ...editForm, target_open_date: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-stone-700">Notes</Label>
                <textarea
                  value={editForm.notes || ""}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  rows={2}
                  className="mt-1 flex w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-900/5 focus:border-stone-400"
                />
              </div>
              <Button type="submit" className="w-full bg-stone-900 text-stone-50 hover:bg-stone-800">
                Update Store
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-6">
        <div className="overflow-x-auto -mx-6 px-6 md:mx-0 md:px-0">
          <TabsList className="bg-stone-100 p-1 rounded-lg inline-flex min-w-max">
            <TabsTrigger data-testid="store-tab-overview" value="overview" className="data-[state=active]:bg-white data-[state=active]:shadow-sm text-sm whitespace-nowrap">
              Overview
            </TabsTrigger>
            <TabsTrigger data-testid="store-tab-timeline" value="timeline" className="data-[state=active]:bg-white data-[state=active]:shadow-sm text-sm whitespace-nowrap">
              Timeline ({totalTasks})
            </TabsTrigger>
            <TabsTrigger data-testid="store-tab-equipment" value="equipment" className="data-[state=active]:bg-white data-[state=active]:shadow-sm text-sm whitespace-nowrap">
              Equipment ({storeEquipment.length})
            </TabsTrigger>
            <TabsTrigger data-testid="store-tab-interiors" value="interiors" className="data-[state=active]:bg-white data-[state=active]:shadow-sm text-sm whitespace-nowrap">
              Interiors ({storeImages.length})
            </TabsTrigger>
            <TabsTrigger data-testid="store-tab-staff" value="staff" className="data-[state=active]:bg-white data-[state=active]:shadow-sm text-sm whitespace-nowrap">
              <Users className="w-4 h-4 mr-1.5 inline" />Staff
            </TabsTrigger>
            <TabsTrigger data-testid="store-tab-financials" value="financials" className="data-[state=active]:bg-white data-[state=active]:shadow-sm text-sm whitespace-nowrap">
              <BarChart3 className="w-4 h-4 mr-1.5 inline" />Financials
            </TabsTrigger>
            <TabsTrigger data-testid="store-tab-documents" value="documents" className="data-[state=active]:bg-white data-[state=active]:shadow-sm text-sm whitespace-nowrap">
              <FolderOpen className="w-4 h-4 mr-1.5 inline" />Documents
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-white border border-stone-200/60 shadow-sm rounded-xl">
              <CardContent className="p-5">
                <p className="text-xs text-stone-500 font-medium uppercase tracking-wide mb-1">Target Opening</p>
                {store.target_open_date ? (
                  <>
                    <p className="text-2xl font-semibold text-stone-900">
                      {new Date(store.target_open_date + "T12:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                    </p>
                    <p className={`text-sm mt-1 ${daysUntil < 0 ? "text-red-600" : daysUntil < 30 ? "text-amber-600" : "text-stone-500"}`}>
                      {daysUntil < 0 ? `${Math.abs(daysUntil)} days overdue` : `${daysUntil} days away`}
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-stone-400">Not set</p>
                )}
              </CardContent>
            </Card>

            <Card className="bg-white border border-stone-200/60 shadow-sm rounded-xl">
              <CardContent className="p-5">
                <p className="text-xs text-stone-500 font-medium uppercase tracking-wide mb-1">Timeline Progress</p>
                <p className="text-2xl font-semibold text-stone-900">{progressPct}%</p>
                <div className="w-full h-2 bg-stone-100 rounded-full mt-2 overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full transition-all duration-500" style={{ width: `${progressPct}%` }} />
                </div>
                <p className="text-xs text-stone-400 mt-1">{completedTasks} of {totalTasks} tasks</p>
              </CardContent>
            </Card>

            <Card className="bg-white border border-stone-200/60 shadow-sm rounded-xl">
              <CardContent className="p-5">
                <p className="text-xs text-stone-500 font-medium uppercase tracking-wide mb-1">Equipment</p>
                <p className="text-2xl font-semibold text-stone-900">{storeEquipment.length} items</p>
                <p className="text-xs text-stone-400 mt-1">${totalEquipmentCost.toLocaleString()} total cost</p>
              </CardContent>
            </Card>

            <Card className="bg-white border border-stone-200/60 shadow-sm rounded-xl">
              <CardContent className="p-5">
                <p className="text-xs text-stone-500 font-medium uppercase tracking-wide mb-1">Interior Designs</p>
                <p className="text-2xl font-semibold text-stone-900">{savedDesigns.length} saved</p>
                <p className="text-xs text-stone-400 mt-1">{storeImages.length} store images uploaded</p>
              </CardContent>
            </Card>
          </div>

          {store.notes && (
            <Card className="bg-white border border-stone-200/60 shadow-sm rounded-xl mt-4">
              <CardContent className="p-5">
                <p className="text-xs text-stone-500 font-medium uppercase tracking-wide mb-2">Notes</p>
                <p className="text-sm text-stone-700">{store.notes}</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Timeline Tab */}
        <TabsContent value="timeline" className="mt-6">
          {/* Actions */}
          <div className="flex items-center gap-3 mb-6 flex-wrap">
            <Button
              data-testid="generate-timeline-btn"
              onClick={generateTimeline}
              disabled={generating || !store.target_open_date}
              className="bg-stone-900 text-stone-50 hover:bg-stone-800"
            >
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {generating ? "Generating..." : "AI Generate Timeline"}
            </Button>
            <Button
              data-testid="add-task-btn"
              onClick={() => { setEditingTask(null); setTaskForm({ title: "", description: "", category: "general", due_date: "", status: "pending" }); setTaskDialogOpen(true); }}
              variant="outline"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Task
            </Button>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-48 text-sm"><SelectValue placeholder="Filter category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {TASK_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-sm text-stone-400 ml-auto">{filteredTasks.length} tasks</p>
          </div>

          {/* Task List */}
          {totalTasks === 0 ? (
            <div className="text-center py-16 bg-stone-50 rounded-xl border-2 border-dashed border-stone-200">
              <CalendarClock className="w-10 h-10 text-stone-300 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-stone-700 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>No tasks yet</h3>
              <p className="text-sm text-stone-500 max-w-md mx-auto">
                {store.target_open_date
                  ? "Click 'AI Generate Timeline' to create a comprehensive task list."
                  : "Set a target opening date first, then generate a timeline."}
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              {Object.entries(grouped).map(([monthKey, monthTasks]) => (
                <div key={monthKey}>
                  <h3 className="text-base font-semibold text-stone-900 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {formatMonth(monthKey)}
                    <span className="text-xs text-stone-400 font-normal ml-2">{monthTasks.length} tasks</span>
                  </h3>
                  <div className="space-y-2">
                    {monthTasks.map((task) => (
                      <div
                        key={task.id}
                        data-testid={`task-item-${task.id}`}
                        className={`flex items-start gap-3 px-4 py-3 bg-white border border-stone-200/60 rounded-xl shadow-sm ${task.status === "completed" ? "opacity-60" : ""}`}
                      >
                        <button onClick={() => toggleTaskStatus(task)} className="mt-0.5 flex-shrink-0 hover:scale-110 transition-transform">
                          {getStatusIcon(task.status)}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start gap-2 flex-wrap">
                            <p className={`text-sm font-medium ${task.status === "completed" ? "line-through text-stone-400" : "text-stone-900"}`}>
                              {task.title}
                            </p>
                            <span className={`inline-flex items-center px-1.5 py-0 rounded text-[10px] font-medium border ${CATEGORY_COLORS[task.category] || CATEGORY_COLORS.general}`}>
                              {TASK_CATEGORIES.find(c => c.value === task.category)?.label || task.category}
                            </span>
                          </div>
                          {task.description && <p className="text-xs text-stone-500 mt-1">{task.description}</p>}
                          {task.due_date && <p className="text-xs text-stone-400 mt-1">{new Date(task.due_date + "T12:00:00").toLocaleDateString()}</p>}
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          <Button variant="ghost" size="sm" onClick={() => openEditTask(task)} className="text-stone-400 hover:text-stone-700 px-1.5">
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteTask(task.id)} className="text-stone-400 hover:text-red-600 px-1.5">
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Task Dialog */}
          <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>
                  {editingTask ? "Edit Task" : "Add Task"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleTaskSubmit} className="space-y-3 mt-3">
                <div>
                  <Label className="text-xs font-medium text-stone-600">Title *</Label>
                  <Input value={taskForm.title} onChange={e => setTaskForm({...taskForm, title: e.target.value})} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-medium text-stone-600">Description</Label>
                  <textarea value={taskForm.description} onChange={e => setTaskForm({...taskForm, description: e.target.value})} rows={2} className="mt-1 flex w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-medium text-stone-600">Category</Label>
                    <Select value={taskForm.category} onValueChange={v => setTaskForm({...taskForm, category: v})}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{TASK_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-stone-600">Due Date</Label>
                    <Input type="date" value={taskForm.due_date} onChange={e => setTaskForm({...taskForm, due_date: e.target.value})} className="mt-1" />
                  </div>
                </div>
                <Button type="submit" className="w-full bg-stone-900 text-stone-50 hover:bg-stone-800">
                  {editingTask ? "Update Task" : "Add Task"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Equipment Tab */}
        <TabsContent value="equipment" className="mt-6">
          {/* Actions */}
          <div className="flex items-center gap-3 mb-6 flex-wrap">
            <Button data-testid="assign-from-catalog-btn" onClick={openAssignDialog} variant="outline" className="border-stone-300 text-stone-700 hover:bg-stone-50">
              <Package className="w-4 h-4 mr-2" /> Assign from Catalog
            </Button>
            <Dialog open={equipmentDialogOpen} onOpenChange={(open) => { if (!open) closeEquipmentDialog(); else setEquipmentDialogOpen(true); }}>
              <DialogTrigger asChild>
                <Button data-testid="add-store-equipment-btn" onClick={openNewEquipment} className="bg-stone-900 text-stone-50 hover:bg-stone-800">
                  <Plus className="w-4 h-4 mr-2" /> Add Custom
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>
                    {editingEquipment ? "Edit Equipment" : equipmentStep === "url" ? "Add Equipment" : "Equipment Details"}
                  </DialogTitle>
                </DialogHeader>

                {/* Step 1: URL Input */}
                {equipmentStep === "url" && !editingEquipment && (
                  <div className="space-y-4 mt-4">
                    <div className="bg-stone-50 border border-stone-200/60 rounded-xl p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <Globe className="w-4 h-4 text-amber-600" />
                        <p className="text-sm font-medium text-stone-700">Paste a product URL</p>
                      </div>
                      <p className="text-xs text-stone-500 mb-3">
                        Paste a link to the equipment and we'll automatically extract details.
                      </p>
                      <div className="flex gap-2">
                        <Input
                          data-testid="eq-scan-url-input"
                          value={scanUrl}
                          onChange={(e) => setScanUrl(e.target.value)}
                          placeholder="https://www.lamarzocco.com/linea-pb"
                          className="flex-1"
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); scanProductUrl(); } }}
                        />
                        <Button
                          data-testid="eq-scan-url-btn"
                          onClick={scanProductUrl}
                          disabled={scanning || !scanUrl.trim()}
                          className="bg-stone-900 text-stone-50 hover:bg-stone-800 px-4"
                        >
                          {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1.5" /> Scan</>}
                        </Button>
                      </div>
                      {scanning && (
                        <div className="flex items-center gap-2 mt-3 text-xs text-stone-500">
                          <Loader2 className="w-3 h-3 animate-spin" /> Scanning page...
                        </div>
                      )}
                    </div>
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-stone-200"></div></div>
                      <div className="relative flex justify-center"><span className="bg-white px-3 text-xs text-stone-400 uppercase tracking-wide">or</span></div>
                    </div>
                    <Button data-testid="eq-skip-manual-btn" onClick={skipToManualEquipment} variant="outline" className="w-full border-stone-200 text-stone-600 hover:bg-stone-50">
                      Enter details manually <ArrowRight className="w-3.5 h-3.5 ml-2" />
                    </Button>
                  </div>
                )}

                {/* Step 2: Equipment Form */}
                {equipmentStep === "form" && (
                  <form onSubmit={handleEquipmentSubmit} className="space-y-3 mt-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2">
                        <Label className="text-xs font-medium text-stone-600">URL</Label>
                        <Input data-testid="eq-url" value={equipmentForm.url} onChange={e => setEquipmentForm({...equipmentForm, url: e.target.value})} placeholder="https://..." className="mt-1" />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs font-medium text-stone-600">Name *</Label>
                        <Input data-testid="eq-name" value={equipmentForm.name} onChange={e => setEquipmentForm({...equipmentForm, name: e.target.value})} placeholder="e.g., La Marzocco Linea PB" className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs font-medium text-stone-600">Category</Label>
                        <Select value={equipmentForm.category} onValueChange={v => setEquipmentForm({...equipmentForm, category: v})}>
                          <SelectTrigger data-testid="eq-category" className="mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent>{EQUIPMENT_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs font-medium text-stone-600">Status</Label>
                        <Select value={equipmentForm.status} onValueChange={v => setEquipmentForm({...equipmentForm, status: v})}>
                          <SelectTrigger data-testid="eq-status" className="mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent>{EQUIPMENT_STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs font-medium text-stone-600">Brand</Label>
                        <Input data-testid="eq-brand" value={equipmentForm.brand} onChange={e => setEquipmentForm({...equipmentForm, brand: e.target.value})} placeholder="La Marzocco" className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs font-medium text-stone-600">Model</Label>
                        <Input data-testid="eq-model" value={equipmentForm.model_name} onChange={e => setEquipmentForm({...equipmentForm, model_name: e.target.value})} placeholder="Linea PB" className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs font-medium text-stone-600">Cost ($)</Label>
                        <Input data-testid="eq-cost" type="number" step="0.01" value={equipmentForm.cost} onChange={e => setEquipmentForm({...equipmentForm, cost: e.target.value})} placeholder="12500" className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs font-medium text-stone-600">Quantity</Label>
                        <Input data-testid="eq-qty" type="number" value={equipmentForm.quantity} onChange={e => setEquipmentForm({...equipmentForm, quantity: e.target.value})} className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs font-medium text-stone-600">Lead Time (days)</Label>
                        <Input data-testid="eq-leadtime" type="number" value={equipmentForm.lead_time_days} onChange={e => setEquipmentForm({...equipmentForm, lead_time_days: e.target.value})} placeholder="45" className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs font-medium text-stone-600">Vendor</Label>
                        <Input data-testid="eq-vendor" value={equipmentForm.vendor} onChange={e => setEquipmentForm({...equipmentForm, vendor: e.target.value})} placeholder="Vendor name" className="mt-1" />
                      </div>
                    </div>
                    <p className="text-xs font-medium text-stone-500 uppercase tracking-wide pt-2">Specs</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-stone-500">Dimensions</Label>
                        <Input data-testid="eq-dim" value={equipmentForm.spec_dimensions} onChange={e => setEquipmentForm({...equipmentForm, spec_dimensions: e.target.value})} placeholder='35"W x 21"D' className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs text-stone-500">Weight</Label>
                        <Input data-testid="eq-weight" value={equipmentForm.spec_weight} onChange={e => setEquipmentForm({...equipmentForm, spec_weight: e.target.value})} placeholder="152 lbs" className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs text-stone-500">Power</Label>
                        <Input data-testid="eq-power" value={equipmentForm.spec_power} onChange={e => setEquipmentForm({...equipmentForm, spec_power: e.target.value})} placeholder="220V / 30A" className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs text-stone-500">Capacity</Label>
                        <Input data-testid="eq-cap" value={equipmentForm.spec_capacity} onChange={e => setEquipmentForm({...equipmentForm, spec_capacity: e.target.value})} placeholder="2-group" className="mt-1" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs font-medium text-stone-600">Notes</Label>
                      <textarea data-testid="eq-notes" value={equipmentForm.notes} onChange={e => setEquipmentForm({...equipmentForm, notes: e.target.value})} rows={2} placeholder="Additional notes..." className="mt-1 flex w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm" />
                    </div>
                    
                    {/* Upload Images Section */}
                    <div className="mt-3">
                      <Label className="text-xs font-medium text-stone-600">Images</Label>
                      <p className="text-xs text-stone-500 mb-2">Upload photos from your device</p>
                      
                      {/* Existing images (when editing) */}
                      {existingEquipmentImages.length > 0 && (
                        <div className="grid grid-cols-4 gap-2 mb-2">
                          {existingEquipmentImages.map((img) => (
                            <div key={img.id} className="relative aspect-square rounded-lg overflow-hidden border border-stone-200">
                              <img 
                                src={`${img.url}`}
                                alt="Equipment"
                                className="w-full h-full object-cover"
                              />
                              <button
                                type="button"
                                onClick={() => removeExistingEquipmentImage(img.id)}
                                className="absolute top-1 right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600"
                              >
                                <X className="w-3 h-3 text-white" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Pending uploads preview */}
                      {pendingEquipmentImages.length > 0 && (
                        <div className="grid grid-cols-4 gap-2 mb-2">
                          {pendingEquipmentImages.map((file, idx) => (
                            <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border-2 border-dashed border-emerald-300 bg-emerald-50">
                              <img 
                                src={URL.createObjectURL(file)}
                                alt={`Upload ${idx + 1}`}
                                className="w-full h-full object-cover"
                              />
                              <button
                                type="button"
                                onClick={() => removePendingEquipmentImage(idx)}
                                className="absolute top-1 right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600"
                              >
                                <X className="w-3 h-3 text-white" />
                              </button>
                              <span className="absolute bottom-1 left-1 text-[10px] bg-emerald-500 text-white px-1 rounded">New</span>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Upload button */}
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        ref={pendingEquipmentImageRef}
                        onChange={addPendingEquipmentImage}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => pendingEquipmentImageRef.current?.click()}
                        className="w-full border-dashed"
                      >
                        <Upload className="w-4 h-4 mr-2" /> Add Images
                      </Button>
                    </div>
                    
                    {/* Scraped Images Selection */}
                    {scrapedEquipmentImages.length > 0 && !editingEquipment && (
                      <div className="mt-3">
                        <Label className="text-xs font-medium text-stone-600">Product Images ({selectedEquipmentImages.length}/{scrapedEquipmentImages.length} selected)</Label>
                        <p className="text-xs text-stone-500 mb-2">Click to select/deselect images to import</p>
                        <div className="grid grid-cols-4 gap-2 mt-2 max-h-40 overflow-y-auto">
                          {scrapedEquipmentImages.map((imgUrl, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => {
                                setSelectedEquipmentImages(prev => 
                                  prev.includes(imgUrl) 
                                    ? prev.filter(u => u !== imgUrl)
                                    : [...prev, imgUrl]
                                );
                              }}
                              className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                                selectedEquipmentImages.includes(imgUrl)
                                  ? "border-emerald-500 ring-2 ring-emerald-200"
                                  : "border-stone-200 hover:border-stone-400"
                              }`}
                            >
                              <img 
                                src={imgUrl} 
                                alt={`Product ${idx + 1}`}
                                className="w-full h-full object-cover"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                              {selectedEquipmentImages.includes(imgUrl) && (
                                <div className="absolute top-1 right-1 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center">
                                  <Check className="w-3 h-3 text-white" />
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <Button type="submit" data-testid="eq-submit" className="w-full bg-stone-900 text-stone-50 hover:bg-stone-800 font-medium mt-2">
                      {editingEquipment ? "Update" : "Add Equipment"}
                    </Button>
                  </form>
                )}
              </DialogContent>
            </Dialog>

            {/* Assign from Catalog Dialog */}
            <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
              <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>
                    Assign Equipment from Catalog
                  </DialogTitle>
                </DialogHeader>
                <p className="text-sm text-stone-500 mb-4">
                  Select equipment from your master catalog to assign to this store.
                </p>
                {availableEquipment.length === 0 ? (
                  <div className="text-center py-8">
                    <Package className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                    <p className="text-sm text-stone-500">No available equipment in catalog.</p>
                    <p className="text-xs text-stone-400 mt-1">Add items to Admin → Equipment first.</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                    {availableEquipment.map(eq => (
                      <div
                        key={eq.id}
                        data-testid={`catalog-item-${eq.id}`}
                        onClick={() => toggleMasterSelection(eq.id)}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                          selectedMasterIds.includes(eq.id)
                            ? "border-stone-900 bg-stone-50"
                            : "border-stone-200 hover:border-stone-300"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            selectedMasterIds.includes(eq.id)
                              ? "border-stone-900 bg-stone-900"
                              : "border-stone-300"
                          }`}>
                            {selectedMasterIds.includes(eq.id) && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-stone-900 text-sm">{eq.name}</p>
                            <p className="text-xs text-stone-500">
                              {eq.brand && `${eq.brand} · `}
                              {EQUIPMENT_CATEGORIES.find(c => c.value === eq.category)?.label || eq.category}
                              {eq.cost && ` · $${eq.cost.toLocaleString()}`}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-stone-200">
                  <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
                  <Button
                    data-testid="confirm-assign-btn"
                    onClick={assignSelectedEquipment}
                    disabled={selectedMasterIds.length === 0 || assigningEquipment}
                    className="bg-stone-900 text-white hover:bg-stone-800"
                  >
                    {assigningEquipment ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Assign {selectedMasterIds.length > 0 ? `(${selectedMasterIds.length})` : ""}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <Select value={filterEquipmentStatus} onValueChange={setFilterEquipmentStatus}>
              <SelectTrigger data-testid="filter-eq-status" className="w-40 text-sm"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {EQUIPMENT_STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>

            <div className="ml-auto flex items-center gap-4 text-sm text-stone-500">
              <span className="flex items-center gap-1.5"><Package className="w-4 h-4" /> {storeEquipment.length} items</span>
              <span className="flex items-center gap-1.5"><DollarSign className="w-4 h-4" /> ${totalEquipmentCost.toLocaleString()}</span>
            </div>
          </div>

          {/* Equipment List */}
          {storeEquipment.length === 0 ? (
            <div className="text-center py-16 bg-stone-50 rounded-xl border-2 border-dashed border-stone-200">
              <Wrench className="w-10 h-10 text-stone-300 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-stone-700 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>No equipment yet</h3>
              <p className="text-sm text-stone-500 max-w-md mx-auto mb-4">
                Add equipment for this store. You can paste a product URL to auto-extract details.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {storeEquipment.map((eq, idx) => (
                <Card key={eq.id} data-testid={`store-equipment-card-${eq.id}`}
                  className="bg-white border border-stone-200/60 shadow-sm rounded-xl">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-stone-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Wrench className="w-4 h-4 text-stone-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3 mb-1">
                          <div>
                            <h3 className="font-semibold text-stone-900 text-base">{eq.name}</h3>
                            <p className="text-xs text-stone-500">
                              {eq.brand && `${eq.brand} `}{eq.model_name && `${eq.model_name} · `}
                              {EQUIPMENT_CATEGORIES.find(c => c.value === eq.category)?.label || eq.category}
                              {eq.quantity > 1 && ` · Qty: ${eq.quantity}`}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {getEquipmentStatusBadge(eq.status)}
                          </div>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-sm text-stone-600 flex-wrap">
                          {eq.cost != null && (
                            <span className="flex items-center gap-1"><DollarSign className="w-3.5 h-3.5 text-stone-400" /> ${(eq.cost * (eq.quantity || 1)).toLocaleString()}</span>
                          )}
                          {eq.lead_time_days != null && (
                            <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-stone-400" /> {eq.lead_time_days} day lead</span>
                          )}
                          {eq.vendor && <span className="text-stone-400">via {eq.vendor}</span>}
                          {eq.url && (
                            <a href={eq.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-amber-700 hover:text-amber-600" onClick={e => e.stopPropagation()}>
                              <ExternalLink className="w-3.5 h-3.5" /> Link
                            </a>
                          )}
                        </div>
                        {Object.keys(eq.specs || {}).length > 0 && (
                          <div className="flex gap-3 mt-2 text-xs text-stone-500 flex-wrap">
                            {Object.entries(eq.specs).map(([k, v]) => (
                              <span key={k} className="bg-stone-50 px-2 py-0.5 rounded">{k}: {v}</span>
                            ))}
                          </div>
                        )}
                        {/* From Catalog Badge */}
                        {eq.master_id && (
                          <div className="mt-2">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                              <Package className="w-3 h-3" /> From Catalog
                            </span>
                          </div>
                        )}
                        {/* Equipment Images */}
                        <div className="flex items-center gap-2 mt-3">
                          {(eq.images || []).map(img => (
                            <div key={img.id} className="relative group/img w-12 h-12 rounded-lg overflow-hidden bg-stone-100 border border-stone-200">
                              <img src={`${img.url}`} alt="" className="w-full h-full object-cover" />
                              <button onClick={(e) => { e.stopPropagation(); deleteEquipmentImage(eq.id, img.id); }}
                                className="absolute inset-0 bg-black/40 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center">
                                <Trash2 className="w-3 h-3 text-white" />
                              </button>
                            </div>
                          ))}
                          <label data-testid={`upload-store-eq-img-${eq.id}`}
                            className="w-12 h-12 rounded-lg border-2 border-dashed border-stone-200 hover:border-stone-400 flex items-center justify-center cursor-pointer transition-colors">
                            <Upload className="w-4 h-4 text-stone-400" />
                            <input type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files?.[0]) uploadEquipmentImage(eq.id, e.target.files[0]); e.target.value = ""; }} />
                          </label>
                        </div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button data-testid={`duplicate-store-eq-${eq.id}`} variant="ghost" size="sm" onClick={() => duplicateEquipment(eq)} className="text-stone-400 hover:text-stone-700 px-2" title="Duplicate">
                          <CopyPlus className="w-3.5 h-3.5" />
                        </Button>
                        <Button data-testid={`edit-store-eq-${eq.id}`} variant="ghost" size="sm" onClick={() => openEditEquipment(eq)} className="text-stone-400 hover:text-stone-700 px-2" title="Edit">
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                        {eq.master_id ? (
                          <Button data-testid={`unassign-store-eq-${eq.id}`} variant="ghost" size="sm" onClick={() => unassignEquipment(eq.id)} className="text-stone-400 hover:text-orange-600 px-2" title="Remove from store">
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        ) : (
                          <Button data-testid={`delete-store-eq-${eq.id}`} variant="ghost" size="sm" onClick={() => deleteEquipment(eq.id)} className="text-stone-400 hover:text-red-600 px-2" title="Delete">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Interiors Tab */}
        <TabsContent value="interiors" className="mt-6">
          {/* Interior Sub-tabs */}
          <div className="flex gap-1 mb-6 bg-stone-100 rounded-lg p-1 max-w-2xl">
            {[
              { id: "gallery", label: "Store Images" },
              { id: "furniture", label: `Furniture (${furniture.length})` },
              { id: "generate", label: "Generate Design" },
              { id: "saved", label: `Saved (${savedDesigns.length})` },
            ].map(t => (
              <button
                key={t.id}
                data-testid={`interior-tab-${t.id}`}
                onClick={() => setInteriorTab(t.id)}
                className={`flex-1 text-sm px-4 py-2 rounded-md transition-all font-medium ${interiorTab === t.id ? "bg-white text-stone-900 shadow-sm" : "text-stone-500 hover:text-stone-700"}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Gallery */}
          {interiorTab === "gallery" && (
            <div className="max-w-4xl">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-stone-500">{storeImages.length} image{storeImages.length !== 1 ? "s" : ""}</p>
                <div>
                  <input ref={multiFileRef} type="file" accept="image/*" multiple onChange={uploadStoreImages} className="hidden" />
                  <Button data-testid="upload-store-images-btn" onClick={() => multiFileRef.current?.click()} disabled={uploading} className="bg-stone-900 text-stone-50 hover:bg-stone-800">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                    Upload Images
                  </Button>
                </div>
              </div>
              {storeImages.length === 0 ? (
                <div className="bg-stone-50 border-2 border-dashed border-stone-200 rounded-xl py-16 text-center">
                  <ImageIcon className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                  <p className="text-sm text-stone-400">No images yet. Upload photos or floorplans of this store.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {storeImages.map((img, idx) => (
                    <div key={img.id} data-testid={`store-image-${img.id}`} className="group relative rounded-xl overflow-hidden bg-stone-100 aspect-square cursor-pointer" onClick={() => openLightbox(idx)}>
                      <img src={`${img.url}`} alt={img.label} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex flex-col justify-between">
                        {/* Zoom indicator */}
                        <div className="p-2 flex justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                          <span className="text-white bg-black/50 p-1.5 rounded-lg">
                            <ZoomIn className="w-4 h-4" />
                          </span>
                        </div>
                        {/* Bottom bar with info and actions */}
                        <div className="w-full p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="flex flex-col gap-1.5">
                            {/* Tags */}
                            {img.tags?.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {img.tags.slice(0, 3).map(tag => (
                                  <span key={tag} className="text-[10px] text-white bg-black/50 px-1.5 py-0.5 rounded">{tag}</span>
                                ))}
                                {img.tags.length > 3 && <span className="text-[10px] text-white/70">+{img.tags.length - 3}</span>}
                              </div>
                            )}
                            {/* Label and actions */}
                            <div className="flex justify-between items-end">
                              <span className="text-xs text-white bg-black/50 px-2 py-1 rounded truncate max-w-[60%]">{img.label}</span>
                              <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                                <Button variant="ghost" size="sm" onClick={() => openEditImage(img)} className="text-white hover:text-blue-300 bg-black/30 hover:bg-black/50 px-1.5 h-7">
                                  <Pencil className="w-3.5 h-3.5" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => deleteStoreImage(img.id)} className="text-white hover:text-red-300 bg-black/30 hover:bg-black/50 px-1.5 h-7">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Furniture Section */}
          {interiorTab === "furniture" && (
            <div className="max-w-5xl space-y-4">
              {/* Header with filters */}
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  {/* Search */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <Input
                      data-testid="furniture-search"
                      placeholder="Search furniture..."
                      value={furnitureSearchQuery}
                      onChange={(e) => setFurnitureSearchQuery(e.target.value)}
                      className="pl-9 w-48"
                    />
                  </div>
                  {/* Status Filter */}
                  <Select value={filterFurnitureStatus} onValueChange={setFilterFurnitureStatus}>
                    <SelectTrigger data-testid="filter-furniture-status" className="w-36">
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      {FURNITURE_STATUS_OPTIONS.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {/* Tag Filter */}
                  <Select value={filterFurnitureTag} onValueChange={setFilterFurnitureTag}>
                    <SelectTrigger data-testid="filter-furniture-tag" className="w-36">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {FURNITURE_TAGS.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-stone-500">
                    Total: <span className="font-semibold text-stone-900">${totalFurnitureCost.toLocaleString()}</span>
                  </span>
                  <Button variant="outline" onClick={() => setShareDialogOpen(true)} className="border-stone-300">
                    <Share2 className="w-4 h-4 mr-1.5" /> Share
                  </Button>
                  <Button data-testid="add-furniture-btn" onClick={openNewFurniture} className="bg-stone-900 text-stone-50 hover:bg-stone-800">
                    <Plus className="w-4 h-4 mr-1.5" /> Add Furniture
                  </Button>
                </div>
              </div>

              {/* Furniture Grid */}
              {filteredFurniture.length === 0 ? (
                <Card className="bg-stone-50 border-2 border-dashed border-stone-200 rounded-xl">
                  <CardContent className="py-16 text-center">
                    <Sofa className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                    <p className="text-sm text-stone-400 mb-4">
                      {furniture.length === 0 
                        ? "No furniture added yet. Add items manually or via URL."
                        : "No furniture matches your filters."
                      }
                    </p>
                    {furniture.length === 0 && (
                      <Button variant="outline" onClick={openNewFurniture}>
                        <Plus className="w-4 h-4 mr-1.5" /> Add First Item
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredFurniture.map(item => (
                    <Card key={item.id} data-testid={`furniture-item-${item.id}`} className="bg-white border border-stone-200/60 rounded-xl overflow-hidden hover:shadow-md transition-shadow">
                      {/* Image or Placeholder */}
                      <div className="relative aspect-[4/3] bg-stone-100">
                        {item.images?.length > 0 ? (
                          <img 
                            src={`${item.images[0].url}`} 
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Sofa className="w-12 h-12 text-stone-300" />
                          </div>
                        )}
                        {/* Status Badge */}
                        <div className="absolute top-2 left-2">
                          {getFurnitureStatusBadge(item.status)}
                        </div>
                        {/* Actions */}
                        <div className="absolute top-2 right-2 flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => duplicateFurniture(item)}
                            className="bg-white/90 hover:bg-white text-stone-700 px-2 h-7"
                            title="Duplicate"
                          >
                            <CopyPlus className="w-3.5 h-3.5" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => openEditFurniture(item)}
                            className="bg-white/90 hover:bg-white text-stone-700 px-2 h-7"
                            title="Edit"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => deleteFurniture(item.id)}
                            className="bg-white/90 hover:bg-white text-red-600 hover:text-red-700 px-2 h-7"
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                        {/* Upload image button */}
                        {item.images?.length === 0 && (
                          <div className="absolute bottom-2 right-2">
                            <input 
                              type="file" 
                              accept="image/*" 
                              className="hidden"
                              id={`furn-img-${item.id}`}
                              onChange={(e) => e.target.files?.[0] && uploadFurnitureImage(item.id, e.target.files[0])}
                            />
                            <label htmlFor={`furn-img-${item.id}`}>
                              <Button variant="outline" size="sm" className="bg-white/90 hover:bg-white cursor-pointer" asChild>
                                <span><Upload className="w-3.5 h-3.5 mr-1" /> Add Photo</span>
                              </Button>
                            </label>
                          </div>
                        )}
                      </div>
                      
                      {/* Content */}
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h4 className="font-semibold text-stone-900 text-sm truncate">{item.name}</h4>
                          {item.price && (
                            <span className="text-sm font-semibold text-emerald-700">${item.price.toLocaleString()}</span>
                          )}
                        </div>
                        
                        {item.vendor && (
                          <p className="text-xs text-stone-500 mb-2">{item.vendor}</p>
                        )}
                        
                        {/* Tags */}
                        {item.tags?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-3">
                            {item.tags.map(tag => (
                              <span key={tag} className="text-[10px] px-1.5 py-0.5 bg-stone-100 text-stone-600 rounded">
                                {FURNITURE_TAGS.find(t => t.value === tag)?.label || tag}
                              </span>
                            ))}
                          </div>
                        )}
                        
                        {/* Details */}
                        <div className="grid grid-cols-2 gap-2 text-xs text-stone-500 mb-3">
                          {item.quantity > 1 && (
                            <span>Qty: {item.quantity}</span>
                          )}
                          {item.lead_time_weeks && (
                            <span>Lead: {item.lead_time_weeks} wks</span>
                          )}
                          {item.dimensions && (
                            <span className="col-span-2 truncate">{item.dimensions}</span>
                          )}
                        </div>
                        
                        {/* Status Actions */}
                        <div className="flex flex-wrap gap-1.5 pt-2 border-t border-stone-100">
                          {item.status === "potential" && (
                            <>
                              <Button size="sm" variant="outline" onClick={() => openFurnitureQtyModal(item)} className="text-xs h-7 px-2">
                                <Check className="w-3 h-3 mr-1" /> Select
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => updateFurnitureStatus(item.id, "rejected")} className="text-xs h-7 px-2 text-red-600 hover:text-red-700">
                                <X className="w-3 h-3 mr-1" /> Reject
                              </Button>
                            </>
                          )}
                          {item.status === "selected" && (
                            <>
                              <Button size="sm" variant="outline" onClick={() => updateFurnitureStatus(item.id, "ordered")} className="text-xs h-7 px-2">
                                <Package className="w-3 h-3 mr-1" /> Mark Ordered
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => openFurnitureQtyModal(item)} className="text-xs h-7 px-2">
                                Qty: {item.selected_quantity || item.quantity || 1}
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => updateFurnitureStatus(item.id, "potential")} className="text-xs h-7 px-2 text-stone-500">
                                Deselect
                              </Button>
                            </>
                          )}
                          {item.status === "ordered" && (
                            <Button size="sm" variant="outline" onClick={() => updateFurnitureStatus(item.id, "delivered")} className="text-xs h-7 px-2 text-emerald-600">
                              <CheckCircle2 className="w-3 h-3 mr-1" /> Mark Delivered
                            </Button>
                          )}
                          {item.status === "delivered" && (
                            <span className="text-xs text-emerald-600 flex items-center gap-1">
                              <CheckCircle2 className="w-3.5 h-3.5" /> Delivered
                            </span>
                          )}
                          {item.status === "rejected" && (
                            <Button size="sm" variant="outline" onClick={() => updateFurnitureStatus(item.id, "potential")} className="text-xs h-7 px-2">
                              Reconsider
                            </Button>
                          )}
                          {item.url && (
                            <a href={item.url} target="_blank" rel="noopener noreferrer" className="ml-auto">
                              <Button size="sm" variant="ghost" className="text-xs h-7 px-2">
                                <ExternalLink className="w-3 h-3 mr-1" /> View
                              </Button>
                            </a>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Generate Design */}
          {interiorTab === "generate" && (
            <div className="max-w-4xl space-y-5">
              {!currentDesign ? (
                <>
                  {/* Select store images */}
                  <Card className="bg-white border border-stone-200/60 rounded-xl">
                    <CardContent className="p-5">
                      <h3 className="text-sm font-semibold text-stone-900 mb-3 flex items-center gap-2">
                        <ImageIcon className="w-4 h-4 text-stone-500" /> Select Store Images
                      </h3>
                      {storeImages.length === 0 ? (
                        <p className="text-xs text-stone-400">Upload store images in the Gallery tab first.</p>
                      ) : (
                        <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                          {storeImages.map(img => (
                            <button
                              key={img.id}
                              data-testid={`select-store-img-${img.id}`}
                              onClick={() => toggleStoreImg(img.id)}
                              className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${selectedStoreImgs.includes(img.id) ? "border-stone-900 ring-2 ring-stone-900/20" : "border-transparent hover:border-stone-300"}`}
                            >
                              <img src={`${img.url}`} alt="" className="w-full h-full object-cover" />
                              {selectedStoreImgs.includes(img.id) && (
                                <div className="absolute top-1 right-1 w-5 h-5 bg-stone-900 rounded-full flex items-center justify-center">
                                  <Check className="w-3 h-3 text-white" />
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Select equipment */}
                  {equipment.length > 0 && (
                    <Card className="bg-white border border-stone-200/60 rounded-xl">
                      <CardContent className="p-5">
                        <h3 className="text-sm font-semibold text-stone-900 mb-3">Select Equipment to Include</h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {equipment.map(eq => (
                            <button
                              key={eq.id}
                              data-testid={`select-equip-${eq.id}`}
                              onClick={() => toggleEquip(eq.id)}
                              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-all ${selectedEquipIds.includes(eq.id) ? "border-stone-900 bg-stone-50" : "border-stone-200 hover:border-stone-300"}`}
                            >
                              {eq.images?.length > 0 ? (
                                <img src={`${eq.images[0].url}`} alt="" className="w-9 h-9 rounded object-cover flex-shrink-0" />
                              ) : (
                                <div className="w-9 h-9 rounded bg-stone-200 flex items-center justify-center flex-shrink-0">
                                  <Store className="w-4 h-4 text-stone-400" />
                                </div>
                              )}
                              <div className="min-w-0 flex-1">
                                <p className="text-xs font-medium text-stone-800 truncate">{eq.name}</p>
                                <p className="text-xs text-stone-400 truncate">{eq.brand} {eq.model_name}</p>
                              </div>
                              {selectedEquipIds.includes(eq.id) && <Check className="w-4 h-4 text-stone-900 flex-shrink-0" />}
                            </button>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Reference Files Upload */}
                  <Card className="bg-white border border-stone-200/60 rounded-xl">
                    <CardContent className="p-5">
                      <h3 className="text-sm font-semibold text-stone-900 mb-3 flex items-center gap-2">
                        <Upload className="w-4 h-4 text-stone-500" /> Additional Reference Images
                      </h3>
                      <p className="text-xs text-stone-500 mb-3">Upload inspiration images, mood boards, or design references (max 5)</p>
                      
                      {referenceFiles.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {referenceFiles.map((file, idx) => (
                            <div key={idx} className="relative group">
                              <img 
                                src={URL.createObjectURL(file)} 
                                alt={file.name}
                                className="w-16 h-16 object-cover rounded-lg border border-stone-200"
                              />
                              <button
                                onClick={() => removeReferenceFile(idx)}
                                className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="w-3 h-3" />
                              </button>
                              <p className="text-[10px] text-stone-400 truncate max-w-[64px] mt-0.5">{file.name}</p>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      <input 
                        ref={referenceFileRef} 
                        type="file" 
                        accept="image/*" 
                        multiple 
                        onChange={addReferenceFiles} 
                        className="hidden" 
                      />
                      <Button
                        data-testid="add-reference-files-btn"
                        variant="outline"
                        size="sm"
                        onClick={() => referenceFileRef.current?.click()}
                        disabled={referenceFiles.length >= 5}
                        className="text-xs"
                      >
                        <Plus className="w-3.5 h-3.5 mr-1.5" />
                        {referenceFiles.length > 0 ? `Add More (${referenceFiles.length}/5)` : "Add Reference Images"}
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Prompt */}
                  <Card className="bg-white border border-stone-200/60 rounded-xl">
                    <CardContent className="p-5">
                      <h3 className="text-sm font-semibold text-stone-900 mb-3 flex items-center gap-2">
                        <Paintbrush className="w-4 h-4 text-stone-500" /> Design Prompt
                      </h3>
                      <Textarea
                        data-testid="design-prompt"
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        placeholder="Describe the interior design you want... e.g., 'Modern minimalist with warm wood tones, exposed brick walls, industrial lighting'"
                        className="min-h-[100px] text-sm"
                      />
                      <Button
                        data-testid="generate-design-btn"
                        onClick={() => generateDesign(false)}
                        disabled={generatingDesign || !prompt.trim()}
                        className="mt-3 bg-stone-900 text-stone-50 hover:bg-stone-800 w-full"
                      >
                        {generatingDesign ? (
                          <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generating...</>
                        ) : (
                          <><Paintbrush className="w-4 h-4 mr-2" /> Generate Interior Design</>
                        )}
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Recent designs */}
                  {designs.length > 0 && (
                    <Card className="bg-white border border-stone-200/60 rounded-xl">
                      <CardContent className="p-5">
                        <h3 className="text-sm font-semibold text-stone-900 mb-3">Recent Designs</h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {designs.slice(0, 6).map(d => (
                            <button
                              key={d.id}
                              data-testid={`design-${d.id}`}
                              onClick={() => setCurrentDesign(d)}
                              className="relative group rounded-lg overflow-hidden bg-stone-100 aspect-square border border-stone-200 hover:border-stone-400 transition-all"
                            >
                              <img src={`${d.url}`} alt="" className="w-full h-full object-cover" />
                              {d.saved && (
                                <div className="absolute top-2 right-2 w-5 h-5 bg-emerald-600 rounded-full flex items-center justify-center">
                                  <BookmarkCheck className="w-3 h-3 text-white" />
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              ) : (
                /* Design result + refinement */
                <div className="space-y-4">
                  <Card className="bg-white border border-stone-200/60 rounded-xl overflow-hidden">
                    <div className="relative">
                      <img
                        src={currentDesign.image_base64 ? `data:image/png;base64,${currentDesign.image_base64}` : `${currentDesign.url}`}
                        alt="Generated design"
                        className="w-full"
                        data-testid="generated-design-image"
                      />
                    </div>
                    <CardContent className="p-5">
                      <p className="text-sm text-stone-600 mb-4"><strong>Prompt:</strong> {currentDesign.prompt}</p>
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          data-testid="save-design-btn"
                          onClick={() => currentDesign.saved ? unsaveDesign(currentDesign.id) : saveDesign(currentDesign.id)}
                          variant={currentDesign.saved ? "default" : "outline"}
                          size="sm"
                          className={currentDesign.saved ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}
                        >
                          {currentDesign.saved ? <><BookmarkCheck className="w-3.5 h-3.5 mr-1" /> Saved</> : <><Bookmark className="w-3.5 h-3.5 mr-1" /> Save Design</>}
                        </Button>
                        <Button data-testid="new-design-btn" variant="outline" size="sm" onClick={() => setCurrentDesign(null)}>
                          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Start Fresh
                        </Button>
                        <Button data-testid="delete-design-btn" variant="outline" size="sm" onClick={() => deleteDesign(currentDesign.id)} className="text-red-600 border-red-200">
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Refinement */}
                  <Card className="bg-white border border-stone-200/60 rounded-xl">
                    <CardContent className="p-5">
                      <h3 className="text-sm font-semibold text-stone-900 mb-3 flex items-center gap-2">
                        <RefreshCw className="w-4 h-4 text-stone-500" /> Refine This Design
                      </h3>
                      <Textarea
                        data-testid="refinement-prompt"
                        value={refinementPrompt}
                        onChange={(e) => setRefinementPrompt(e.target.value)}
                        placeholder="What would you like to change? e.g., 'Make the lighting warmer, add more plants'"
                        className="min-h-[80px] text-sm"
                      />
                      <Button
                        data-testid="refine-design-btn"
                        onClick={() => generateDesign(true)}
                        disabled={generatingDesign || !refinementPrompt.trim()}
                        className="mt-3 bg-stone-900 text-stone-50 hover:bg-stone-800 w-full"
                      >
                        {generatingDesign ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Refining...</> : <><Paintbrush className="w-4 h-4 mr-2" /> Refine Design</>}
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          )}

          {/* Saved Designs */}
          {interiorTab === "saved" && (
            <div className="max-w-4xl">
              {savedDesigns.length === 0 ? (
                <div className="bg-stone-50 border-2 border-dashed border-stone-200 rounded-xl py-16 text-center">
                  <BookmarkCheck className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                  <p className="text-sm text-stone-400">No saved designs yet. Generate and save designs you love.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {savedDesigns.map(d => (
                    <Card key={d.id} data-testid={`saved-design-${d.id}`} className="bg-white border border-stone-200/60 rounded-xl overflow-hidden">
                      <img src={`${d.url}`} alt="" className="w-full aspect-video object-cover" />
                      <CardContent className="p-4">
                        <p className="text-sm text-stone-600 mb-3 line-clamp-2">{d.prompt}</p>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => { setInteriorTab("generate"); setCurrentDesign(d); }}>
                            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refine
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => unsaveDesign(d.id)} className="text-stone-500">
                            <X className="w-3.5 h-3.5 mr-1" /> Unsave
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => deleteDesign(d.id)} className="text-red-600 border-red-200">
                            <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* Staff Tab */}
        <TabsContent value="staff" className="mt-6">
          <StoreStaffTab storeId={storeId} storeName={store.name} />
        </TabsContent>

        {/* Financials Tab */}
        <TabsContent value="financials" className="mt-6">
          <StoreFinancialsTab storeId={storeId} storeName={store.name} />
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="mt-6">
          <StoreDocumentsTab storeId={storeId} storeName={store.name} />
        </TabsContent>
      </Tabs>

      {/* Lightbox Modal */}
      {lightboxOpen && storeImages.length > 0 && (
        <div 
          data-testid="image-lightbox"
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close button */}
          <button 
            data-testid="lightbox-close"
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white/70 hover:text-white p-2 rounded-lg hover:bg-white/10 transition-colors z-10"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Navigation - Previous */}
          {storeImages.length > 1 && (
            <button
              data-testid="lightbox-prev"
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-3 rounded-full hover:bg-white/10 transition-colors"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>
          )}

          {/* Main Image */}
          <div className="max-w-[90vw] max-h-[85vh] flex flex-col items-center" onClick={e => e.stopPropagation()}>
            <img 
              src={`${storeImages[lightboxIndex]?.url}`}
              alt={storeImages[lightboxIndex]?.label}
              className="max-w-full max-h-[75vh] object-contain rounded-lg"
            />
            
            {/* Image info */}
            <div className="mt-4 text-center">
              <p className="text-white font-medium mb-1">{storeImages[lightboxIndex]?.label}</p>
              {storeImages[lightboxIndex]?.tags?.length > 0 && (
                <div className="flex flex-wrap justify-center gap-1.5 mb-2">
                  {storeImages[lightboxIndex].tags.map(tag => (
                    <span key={tag} className="text-xs text-white/80 bg-white/20 px-2 py-0.5 rounded">{tag}</span>
                  ))}
                </div>
              )}
              <p className="text-white/50 text-sm">{lightboxIndex + 1} of {storeImages.length}</p>
              
              {/* Edit button in lightbox */}
              <Button 
                data-testid="lightbox-edit"
                variant="outline"
                size="sm"
                onClick={() => { openEditImage(storeImages[lightboxIndex]); closeLightbox(); }}
                className="mt-3 bg-white/10 border-white/30 text-white hover:bg-white/20"
              >
                <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
              </Button>
            </div>
          </div>

          {/* Navigation - Next */}
          {storeImages.length > 1 && (
            <button
              data-testid="lightbox-next"
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-3 rounded-full hover:bg-white/10 transition-colors"
            >
              <ChevronRight className="w-8 h-8" />
            </button>
          )}
        </div>
      )}

      {/* Edit Image Dialog */}
      <Dialog open={!!editingImage} onOpenChange={(open) => { if (!open) closeEditImage(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>Edit Image</DialogTitle>
          </DialogHeader>
          
          {editingImage && (
            <div className="space-y-4 mt-2">
              {/* Preview */}
              <div className="aspect-video bg-stone-100 rounded-lg overflow-hidden">
                <img 
                  src={`${editingImage.url}`}
                  alt={editingImage.label}
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Name */}
              <div>
                <Label className="text-xs font-medium text-stone-600">Image Name</Label>
                <Input 
                  data-testid="edit-image-label"
                  value={editImageLabel}
                  onChange={e => setEditImageLabel(e.target.value)}
                  placeholder="Enter image name..."
                  className="mt-1"
                />
              </div>
              
              {/* Tags */}
              <div>
                <Label className="text-xs font-medium text-stone-600">Tags</Label>
                <div className="flex flex-wrap gap-1.5 mt-1.5 mb-2">
                  {editImageTags.map(tag => (
                    <span key={tag} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-stone-100 text-stone-700 rounded-full">
                      {tag}
                      <button onClick={() => removeImageTag(tag)} className="hover:text-red-600">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    data-testid="edit-image-tag-input"
                    value={editTagInput}
                    onChange={e => setEditTagInput(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addImageTag(editTagInput); } }}
                    placeholder="Add tag (e.g., exterior, floor-plan)..."
                    className="text-sm flex-1"
                  />
                  <Button
                    data-testid="add-image-tag-btn"
                    variant="outline"
                    size="sm"
                    onClick={() => addImageTag(editTagInput)}
                    disabled={!editTagInput.trim()}
                    className="px-3"
                  >
                    <Tag className="w-3.5 h-3.5" />
                  </Button>
                </div>
                
                {/* Suggested tags */}
                <div className="flex flex-wrap gap-1 mt-2">
                  {["exterior", "interior", "floor-plan", "kitchen", "seating", "counter", "entrance", "signage", "storage"].filter(t => !editImageTags.includes(t)).slice(0, 5).map(tag => (
                    <button
                      key={tag}
                      onClick={() => addImageTag(tag)}
                      className="px-2 py-0.5 text-xs bg-stone-50 border border-stone-200 text-stone-600 rounded-full hover:bg-stone-100 transition-colors"
                    >
                      + {tag}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button
                  data-testid="save-image-changes"
                  onClick={saveImageChanges}
                  disabled={savingImage}
                  className="flex-1 bg-stone-900 text-stone-50 hover:bg-stone-800"
                >
                  {savingImage ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Check className="w-4 h-4 mr-1.5" />}
                  Save Changes
                </Button>
                <Button variant="outline" onClick={closeEditImage}>Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Furniture Dialog */}
      <Dialog open={furnitureDialogOpen} onOpenChange={(open) => { if (!open) closeFurnitureDialog(); else setFurnitureDialogOpen(true); }}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>
              {editingFurniture ? "Edit Furniture" : "Add Furniture"}
            </DialogTitle>
          </DialogHeader>
          
          {furnitureStep === "url" ? (
            <div className="space-y-4 py-2">
              <p className="text-sm text-stone-500">Enter a product URL to auto-fill details, or add manually.</p>
              <div className="flex gap-2">
                <Input
                  data-testid="furniture-scan-url"
                  value={furnitureScanUrl}
                  onChange={e => setFurnitureScanUrl(e.target.value)}
                  placeholder="https://example.com/product..."
                  className="flex-1"
                />
                <Button onClick={scanFurnitureUrl} disabled={scanningFurniture || !furnitureScanUrl.trim()} className="bg-stone-900 text-stone-50 hover:bg-stone-800">
                  {scanningFurniture ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link className="w-4 h-4" />}
                </Button>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-stone-200" />
                <span className="text-xs text-stone-400">or</span>
                <div className="flex-1 h-px bg-stone-200" />
              </div>
              <Button variant="outline" onClick={skipToManualFurniture} className="w-full">
                Add Manually
              </Button>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs">Name *</Label>
                  <Input
                    data-testid="furniture-name"
                    value={furnitureForm.name}
                    onChange={e => setFurnitureForm(p => ({ ...p, name: e.target.value }))}
                    placeholder="e.g., Dining Chair"
                    className="mt-1"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Description</Label>
                  <Textarea
                    data-testid="furniture-description"
                    value={furnitureForm.description}
                    onChange={e => setFurnitureForm(p => ({ ...p, description: e.target.value }))}
                    placeholder="Brief description..."
                    className="mt-1"
                    rows={2}
                  />
                </div>
              </div>
              
              {/* Price & Quantity */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Price ($)</Label>
                  <Input
                    data-testid="furniture-price"
                    type="number"
                    value={furnitureForm.price}
                    onChange={e => setFurnitureForm(p => ({ ...p, price: e.target.value }))}
                    placeholder="0"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Quantity</Label>
                  <Input
                    data-testid="furniture-quantity"
                    type="number"
                    value={furnitureForm.quantity}
                    onChange={e => setFurnitureForm(p => ({ ...p, quantity: parseInt(e.target.value) || 1 }))}
                    min="1"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Lead Time (weeks)</Label>
                  <Input
                    data-testid="furniture-lead-time"
                    type="number"
                    value={furnitureForm.lead_time_weeks}
                    onChange={e => setFurnitureForm(p => ({ ...p, lead_time_weeks: e.target.value }))}
                    placeholder="4"
                    className="mt-1"
                  />
                </div>
              </div>
              
              {/* Vendor & Dimensions */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Vendor/Supplier</Label>
                  <Input
                    data-testid="furniture-vendor"
                    value={furnitureForm.vendor}
                    onChange={e => setFurnitureForm(p => ({ ...p, vendor: e.target.value }))}
                    placeholder="e.g., IKEA"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Dimensions (W x D x H)</Label>
                  <Input
                    data-testid="furniture-dimensions"
                    value={furnitureForm.dimensions}
                    onChange={e => setFurnitureForm(p => ({ ...p, dimensions: e.target.value }))}
                    placeholder="45cm x 50cm x 85cm"
                    className="mt-1"
                  />
                </div>
              </div>
              
              {/* Material & Color */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Material</Label>
                  <Input
                    data-testid="furniture-material"
                    value={furnitureForm.material}
                    onChange={e => setFurnitureForm(p => ({ ...p, material: e.target.value }))}
                    placeholder="e.g., Oak wood"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Color</Label>
                  <Input
                    data-testid="furniture-color"
                    value={furnitureForm.color}
                    onChange={e => setFurnitureForm(p => ({ ...p, color: e.target.value }))}
                    placeholder="e.g., Natural"
                    className="mt-1"
                  />
                </div>
              </div>
              
              {/* URL */}
              <div>
                <Label className="text-xs">Product URL</Label>
                <Input
                  data-testid="furniture-url"
                  value={furnitureForm.url}
                  onChange={e => setFurnitureForm(p => ({ ...p, url: e.target.value }))}
                  placeholder="https://..."
                  className="mt-1"
                />
              </div>
              
              {/* Tags */}
              <div>
                <Label className="text-xs">Item Type (Tags)</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {FURNITURE_TAGS.map(tag => (
                    <button
                      key={tag.value}
                      type="button"
                      onClick={() => toggleFurnitureTag(tag.value)}
                      className={`px-2.5 py-1 text-xs rounded-full border transition-all ${
                        furnitureForm.tags.includes(tag.value)
                          ? "bg-stone-900 text-white border-stone-900"
                          : "bg-white text-stone-600 border-stone-200 hover:border-stone-400"
                      }`}
                    >
                      {tag.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Status (for editing) */}
              {editingFurniture && (
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select value={furnitureForm.status} onValueChange={v => setFurnitureForm(p => ({ ...p, status: v }))}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FURNITURE_STATUS_OPTIONS.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Scraped Images Selection */}
              {scrapedFurnitureImages.length > 0 && !editingFurniture && (
                <div>
                  <Label className="text-xs">Product Images ({selectedScrapedImages.length}/{scrapedFurnitureImages.length} selected)</Label>
                  <p className="text-xs text-stone-500 mb-2">Click to select/deselect images to import</p>
                  <div className="grid grid-cols-4 gap-2 mt-2 max-h-48 overflow-y-auto">
                    {scrapedFurnitureImages.map((imgUrl, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => {
                          setSelectedScrapedImages(prev => 
                            prev.includes(imgUrl) 
                              ? prev.filter(u => u !== imgUrl)
                              : [...prev, imgUrl]
                          );
                        }}
                        className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                          selectedScrapedImages.includes(imgUrl)
                            ? "border-emerald-500 ring-2 ring-emerald-200"
                            : "border-stone-200 hover:border-stone-400"
                        }`}
                      >
                        <img 
                          src={imgUrl} 
                          alt={`Product ${idx + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        {selectedScrapedImages.includes(imgUrl) && (
                          <div className="absolute top-1 right-1 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Upload Images Section */}
              <div>
                <Label className="text-xs">Images</Label>
                <p className="text-xs text-stone-500 mb-2">Upload photos from your device</p>
                
                {/* Existing images (when editing) */}
                {existingFurnitureImages.length > 0 && (
                  <div className="grid grid-cols-4 gap-2 mb-2">
                    {existingFurnitureImages.map((img) => (
                      <div key={img.id} className="relative aspect-square rounded-lg overflow-hidden border border-stone-200">
                        <img 
                          src={`${img.url}`}
                          alt="Furniture"
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => removeExistingFurnitureImage(img.id)}
                          className="absolute top-1 right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600"
                        >
                          <X className="w-3 h-3 text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Pending uploads preview */}
                {pendingFurnitureImages.length > 0 && (
                  <div className="grid grid-cols-4 gap-2 mb-2">
                    {pendingFurnitureImages.map((file, idx) => (
                      <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border-2 border-dashed border-emerald-300 bg-emerald-50">
                        <img 
                          src={URL.createObjectURL(file)}
                          alt={`Upload ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => removePendingFurnitureImage(idx)}
                          className="absolute top-1 right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600"
                        >
                          <X className="w-3 h-3 text-white" />
                        </button>
                        <span className="absolute bottom-1 left-1 text-[10px] bg-emerald-500 text-white px-1 rounded">New</span>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Upload button */}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  ref={pendingFurnitureImageRef}
                  onChange={addPendingFurnitureImage}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => pendingFurnitureImageRef.current?.click()}
                  className="w-full border-dashed"
                >
                  <Upload className="w-4 h-4 mr-2" /> Add Images
                </Button>
              </div>
              
              {/* Notes */}
              <div>
                <Label className="text-xs">Notes</Label>
                <Textarea
                  data-testid="furniture-notes"
                  value={furnitureForm.notes}
                  onChange={e => setFurnitureForm(p => ({ ...p, notes: e.target.value }))}
                  placeholder="Any additional notes..."
                  className="mt-1"
                  rows={2}
                />
              </div>
              
              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button onClick={saveFurniture} className="flex-1 bg-stone-900 text-stone-50 hover:bg-stone-800">
                  <Check className="w-4 h-4 mr-1.5" />
                  {editingFurniture ? "Update" : "Add"} Furniture
                </Button>
                <Button variant="outline" onClick={closeFurnitureDialog}>Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Share Furniture Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>Share Furniture Collection</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            {!furnitureShare ? (
              <div className="text-center py-6">
                <Share2 className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                <p className="text-sm text-stone-500 mb-4">Create a public link to share this furniture collection with others.</p>
                <Button onClick={createFurnitureShare} disabled={creatingShare} className="bg-stone-900 text-stone-50 hover:bg-stone-800">
                  {creatingShare ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Link className="w-4 h-4 mr-2" />}
                  Create Share Link
                </Button>
              </div>
            ) : (
              <>
                {/* Share URL */}
                <div>
                  <label className="text-xs font-medium text-stone-600">Share URL</label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      readOnly
                      value={`${window.location.origin}/furniture/${furnitureShare.share_token}`}
                      className="text-sm font-mono bg-stone-50"
                    />
                    <Button onClick={copyShareLink} variant="outline" className="px-3">
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                {/* Settings */}
                <div className="space-y-3 pt-2">
                  <div className="flex items-center justify-between py-2 px-3 bg-stone-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-stone-900">Link Active</p>
                      <p className="text-xs text-stone-500">Enable or disable the share link</p>
                    </div>
                    <button onClick={toggleShareActive} className="text-stone-600 hover:text-stone-900">
                      {furnitureShare.is_active ? (
                        <ToggleRight className="w-8 h-8 text-emerald-600" />
                      ) : (
                        <ToggleLeft className="w-8 h-8 text-stone-400" />
                      )}
                    </button>
                  </div>
                  
                  <div className="flex items-center justify-between py-2 px-3 bg-stone-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-stone-900">Allow Voting</p>
                      <p className="text-xs text-stone-500">Let viewers select or reject items</p>
                    </div>
                    <button onClick={toggleVoting} className="text-stone-600 hover:text-stone-900">
                      {furnitureShare.allow_voting ? (
                        <ToggleRight className="w-8 h-8 text-emerald-600" />
                      ) : (
                        <ToggleLeft className="w-8 h-8 text-stone-400" />
                      )}
                    </button>
                  </div>
                </div>
                
                {/* Preview Button */}
                <a href={`/furniture/${furnitureShare.share_token}`} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="w-full mt-2">
                    <ExternalLink className="w-4 h-4 mr-2" /> Preview Public Page
                  </Button>
                </a>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Furniture Quantity Selection Modal */}
      <Dialog open={!!furnitureQtyModalItem} onOpenChange={(open) => !open && setFurnitureQtyModalItem(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>
              Select Quantity
            </DialogTitle>
            <p className="text-sm text-stone-500">Choose how many items to select</p>
          </DialogHeader>
          {furnitureQtyModalItem && (
            <div className="py-4">
              <p className="text-sm text-stone-600 mb-4">
                How many <span className="font-medium">{furnitureQtyModalItem.name}</span> do you want to select?
              </p>
              <p className="text-xs text-stone-500 mb-2">Available: {furnitureQtyModalItem.quantity || 1}</p>
              
              {/* Quantity selector */}
              <div className="flex items-center justify-center gap-4 mb-6">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFurnitureSelectedQty(q => Math.max(1, q - 1))}
                  disabled={furnitureSelectedQty <= 1}
                  className="w-10 h-10"
                >
                  -
                </Button>
                <span className="text-3xl font-bold text-stone-900 w-16 text-center">{furnitureSelectedQty}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFurnitureSelectedQty(q => Math.min(furnitureQtyModalItem.quantity || 1, q + 1))}
                  disabled={furnitureSelectedQty >= (furnitureQtyModalItem.quantity || 1)}
                  className="w-10 h-10"
                >
                  +
                </Button>
              </div>
              
              {/* Total price */}
              {furnitureQtyModalItem.price && (
                <div className="text-center mb-6 p-3 bg-stone-50 rounded-lg">
                  <p className="text-xs text-stone-500 mb-1">Total for selected quantity</p>
                  <p className="text-2xl font-bold text-stone-900">
                    ${(furnitureQtyModalItem.price * furnitureSelectedQty).toLocaleString()}
                  </p>
                </div>
              )}
              
              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  onClick={confirmFurnitureSelection}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Select {furnitureSelectedQty}
                </Button>
                <Button variant="outline" onClick={() => setFurnitureQtyModalItem(null)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
